import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import { getTodayLocalISO, isFutureISO } from '../utils/dateUtils';
import { 
  Plus, 
  Users, 
  Edit,
  Trash2,
  UserCheck,
  FileText,
  BarChart3,
  Settings,
  CheckCircle,
  Download,
  Clock,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Cake,
  Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Garantir formato yyyy-MM-dd para inputs type="date"
const toInputDateValue = (val) => {
  if (!val) return '';
  try {
    if (typeof val === 'string') {
      // já no formato correto
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      // ISO ou contém hora: extrair a parte de data
      const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
      // tentar parse e formatar
      const d = new Date(val);
      if (!isNaN(d)) return format(d, 'yyyy-MM-dd');
      return '';
    }
    if (val instanceof Date) {
      return format(val, 'yyyy-MM-dd');
    }
    if (val && typeof val === 'object' && val.toString) {
      const d = new Date(val.toString());
      if (!isNaN(d)) return format(d, 'yyyy-MM-dd');
    }
  } catch (e) {}
  return '';
};

// Mês (1-12) da data de nascimento, lido em UTC (datas são gravadas como meia-noite UTC)
const birthMonthOf = (val) => (val ? new Date(val).getUTCMonth() + 1 : null);
// Mês atual (1-12) no horário de Brasília
const currentMonthBR = () =>
  Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo', month: 'numeric' }).format(new Date()));

const AVATAR_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#6366f1'];
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0].slice(0, 2).toUpperCase();
};
const getAvatarColor = (name) => AVATAR_COLORS[(name || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const Members = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const canInvite = user?.role === 'secretario' || user?.role === 'administracao' || user?.role === 'pastor' || user?.role === 'tesoureiro';
  
  // Função para formatar data evitando problemas de timezone
  const formatDateSafe = (dateInput) => {
    if (!dateInput) return '';

    const formatLocal = (dateObj) => {
      return isNaN(dateObj.getTime()) ? 'Data inválida' : format(dateObj, 'dd/MM/yyyy', { locale: ptBR });
    };

    // Tratar string no formato YYYY-MM-DD como data LOCAL
    if (typeof dateInput === 'string') {
      const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [_, y, m, d] = match;
        const localDate = new Date(Number(y), Number(m) - 1, Number(d));
        return formatLocal(localDate);
      }
      const parsed = new Date(dateInput);
      return formatLocal(parsed);
    }

    // Se for objeto Date ou timestamp
    const date = new Date(dateInput);
    return formatLocal(date);
  };

  const handleSendInvite = async (member) => {
    try {
      if (!member?.email) {
        toast.error('Membro sem email cadastrado');
        return;
      }
      const res = await api.post('/email/test-send', { email: member.email });
      if (res.data?.success) {
        toast.success('Convite enviado');
      } else {
        toast.error(res.data?.message || 'Falha ao enviar convite');
      }
    } catch (e) {
      const msg = e?.response?.data?.message || 'Erro ao enviar convite';
      toast.error(msg);
    }
  };
  
  const [members, setMembers] = useState([]);

  const [stats, setStats] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewMember, setViewMember] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [showReportModal, setShowReportModal] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);

  const [filters, setFilters] = useState({
    search: '',
    role: '',
    birthdayMonthOnly: false
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    birthDate: '',
    role: 'membro',
    maritalStatus: '',
    gender: ''
  });
  
  // Estados para o novo formulário de cadastro
  const [showCadastroModal, setShowCadastroModal] = useState(false);
  const [cadastroData, setCadastroData] = useState({
    name: '',
    canSendMessage: '',
    phone: '',
    type: '', // 'integra' ou 'decisao'
    visitDate: '',
    isReconciling: '',
    reconcilingDate: '',
    acceptedJesus: '',
    acceptedJesusDate: ''
  });
  
  // Estados para o modal de pré-cadastro
  const [showPreCadastroModal, setShowPreCadastroModal] = useState(false);
  const [preCadastroData, setPreCadastroData] = useState({
    name: '',
    phone: '',
    birthDate: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsTarget, setPermissionsTarget] = useState(null);
  const [permissionsForm, setPermissionsForm] = useState({ fichasEnabled: false, eventsManageEnabled: false, momentsEnabled: false });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  
  // Estados para pré-cadastros
  const [preCadastros, setPreCadastros] = useState([]);
  
  // Estados para visitantes e decisões
  const [visitantes, setVisitantes] = useState([]);
  
  // Estados para edição de pré-cadastros
  const [showEditPreCadastroModal, setShowEditPreCadastroModal] = useState(false);
  const [editingPreCadastro, setEditingPreCadastro] = useState(null);
  const [showDeletePreCadastroModal, setShowDeletePreCadastroModal] = useState(false);
  const [preCadastroToDelete, setPreCadastroToDelete] = useState(null);
  
  // Estados para edição de visitantes e decisões
  const [showEditVisitanteModal, setShowEditVisitanteModal] = useState(false);
  const [showEditDecisaoModal, setShowEditDecisaoModal] = useState(false);
  const [editingVisitante, setEditingVisitante] = useState(null);
  const [editingDecisao, setEditingDecisao] = useState(null);
  const [showDeleteVisitanteModal, setShowDeleteVisitanteModal] = useState(false);
  const [showDeleteDecisaoModal, setShowDeleteDecisaoModal] = useState(false);
  const [visitanteToDelete, setVisitanteToDelete] = useState(null);
  const [decisaoToDelete, setDecisaoToDelete] = useState(null);
  const [decisoes, setDecisoes] = useState([]);
  const [filtroDecisao, setFiltroDecisao] = useState('todas'); // 'todas', 'reconciliacao', 'aceitacao'
  const [filtroAnoVisitante, setFiltroAnoVisitante] = useState('todos');
  const [filtroMesVisitante, setFiltroMesVisitante] = useState('todos');
  const [filtroAnoDecisao, setFiltroAnoDecisao] = useState('todos');
  const [filtroMesDecisao, setFiltroMesDecisao] = useState('todos');


  const allRoles = [
    { value: 'membro', label: 'Membro' },
    { value: 'secretario', label: 'Secretário' },
    { value: 'tesoureiro', label: 'Tesoureiro' },
    { value: 'pastor', label: 'Pastor' },
    { value: 'administracao', label: 'Administração' }
  ];

  // Filtrar roles baseado no perfil do usuário
  const roles = user?.role === 'secretario' 
    ? allRoles.filter(role => ['membro'].includes(role.value))
    : allRoles;
  
  const hasSecretariatOrFichas = Boolean(user?.permissions?.fichasEnabled) || ['secretario','pastor','administracao'].includes(user?.role);
  const canManagePreCadastros = ['secretario','pastor','administracao'].includes(user?.role);

  const loadMembersData = useCallback(async () => {
    try {
      setLoading(true);
      const membersParams = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      const [membersRes, statsRes] = await Promise.all([
        api.get('/members', { params: membersParams }),
        api.get('/members/stats')
      ]);
      
      setMembers(membersRes.data.members || []);
      
      setPagination(prev => ({
        ...prev,
        totalPages: membersRes.data.totalPages || 1,
        total: membersRes.data.total || 0
      }));
      
      console.log('Stats data from API:', statsRes.data);
      console.log('byAgeGroup data:', statsRes.data.byAgeGroup);
      setStats({
        total: statsRes.data.total || 0,
        recentMembers: statsRes.data.recent?.length || 0,
        birthdaysThisMonth: statsRes.data.birthdaysThisMonth || 0,
        celulaLeaders: statsRes.data.celulaLeaders || 0,
        byRole: statsRes.data.byRole || {},
        byGender: statsRes.data.byGender || {},
        byAgeGroup: statsRes.data.byAgeGroup || {}
      });
      console.log('Stats state updated:', {
        byAgeGroup: statsRes.data.byAgeGroup || {}
      });

      
      // Carregar atividades recentes
      const recentMembersData = statsRes.data.recentMembers || [];
      setRecentActivities(recentMembersData.slice(0, 5));
    } catch (error) {
      console.error('Erro ao carregar dados dos membros:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  const loadPreCadastros = useCallback(async () => {
    try {
      if (!canManagePreCadastros) { setPreCadastros([]); return; }
      const response = await api.get('/members/pre-cadastros');
      setPreCadastros(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar pré-cadastros:', error);
    }
  }, []);

  const loadVisitantes = useCallback(async () => {
    try {
      if (!hasSecretariatOrFichas) { setVisitantes([]); return; }
      const response = await api.get('/members/visitantes');
      setVisitantes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar visitantes:', error);
    }
  }, []);

  const loadDecisoes = useCallback(async () => {
    try {
      if (!hasSecretariatOrFichas) { setDecisoes([]); return; }
      const response = await api.get('/members/decisoes');
      setDecisoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar decisões:', error);
    }
  }, []);

  useEffect(() => {
    loadMembersData();
    if (canManagePreCadastros) loadPreCadastros();
    if (hasSecretariatOrFichas) {
      loadVisitantes();
      loadDecisoes();
    } else {
      setVisitantes([]);
      setDecisoes([]);
    }
  }, [loadMembersData, loadPreCadastros, loadVisitantes, loadDecisoes, hasSecretariatOrFichas, canManagePreCadastros]);

  // Estados para estatísticas de aniversários
  const [birthdayStats, setBirthdayStats] = useState({
    members: 0,
    preCadastros: 0,
    total: 0
  });

  // Função para calcular aniversários do mês atual
  const calculateBirthdayStats = useCallback(() => {
    const currentMonth = currentMonthBR();
    
    // Calcular aniversários de membros
    const memberBirthdays = stats?.birthdaysThisMonth || 0;
    
    // Calcular aniversários de pré-cadastros
    const preCadastroBirthdays = preCadastros.filter(preCadastro => {
      if (!preCadastro.birthDate) return false;
      return birthMonthOf(preCadastro.birthDate) === currentMonth;
    }).length;
    
    setBirthdayStats({
      members: memberBirthdays,
      preCadastros: preCadastroBirthdays,
      total: memberBirthdays + preCadastroBirthdays
    });
  }, [stats?.birthdaysThisMonth, preCadastros]);

  // Recalcular estatísticas quando membros ou pré-cadastros mudarem
  useEffect(() => {
    calculateBirthdayStats();
  }, [calculateBirthdayStats]);



  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };



  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const generateReport = async (type) => {
    try {
      const response = await api.get(`/members/report/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${type}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMember) {
        await api.put(`/members/${editingMember._id}`, formData);
      } else {
        await api.post('/users/register', {
          ...formData,
          password: 'senha123' // Senha padrão
        });
      }
      
      setShowModal(false);
      setEditingMember(null);
      resetForm();
      loadMembersData();
    } catch (error) {
      console.error('Erro ao salvar membro:', error);
      
      // Tratar diferentes tipos de erro
      let errorMessage = 'Erro ao salvar membro';
      
      if (error.response?.data?.error === 'EMAIL_ALREADY_EXISTS') {
        errorMessage = error.response.data.message || 'Este email já está sendo usado por outro membro';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações e tente novamente.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Você não tem permissão para realizar esta ação.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
      }
      
      console.error(errorMessage);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone || '',
      address: {
        street: member.address?.street || '',
        city: member.address?.city || '',
        state: member.address?.state || '',
        zipCode: member.address?.zipCode || ''
      },
      birthDate: toInputDateValue(member.birthDate),
      role: member.role,
      maritalStatus: member.maritalStatus || '',
      gender: member.gender || ''
    });
    setShowModal(true);
  };

  const handleView = (member) => {
    setViewMember(member);
    setShowViewModal(true);
  };

  // Preenche rua/cidade/estado automaticamente ao digitar CEP
  const handleCepChange = async (e) => {
    const raw = e.target.value || '';
    const cep = raw.replace(/\D/g, '');

    // Atualiza o CEP imediatamente
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, zipCode: cep }
    }));

    // Busca endereço quando CEP estiver completo (8 dígitos)
    if (cep.length === 8) {
      try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            address: {
              ...prev.address,
              street: data.logradouro || prev.address.street || '',
              city: data.localidade || prev.address.city || '',
              state: data.uf || prev.address.state || ''
            }
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const handleDelete = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const toggleFichasPermission = async (member) => {
    try {
      const current = Boolean(member.permissions?.fichasEnabled);
      const next = !current;
      // ARIA feedback via loading state
      setMembers(prev => prev.map(m => m._id === member._id ? { ...m, __togglingPerm: true } : m));
      const res = await api.put(`/users/${member._id}/permissions`, { fichasEnabled: next });
      setMembers(prev => prev.map(m => m._id === member._id ? { ...res.data.user, __togglingPerm: false } : m));
    } catch (error) {
      setMembers(prev => prev.map(m => m._id === member._id ? { ...m, __togglingPerm: false } : m));
    }
  };

  const openPermissionsModal = (member) => {
    setPermissionsTarget(member);
    setPermissionsForm({
      fichasEnabled: Boolean(member.permissions?.fichasEnabled),
      eventsManageEnabled: Boolean(member.permissions?.eventsManageEnabled),
      momentsEnabled: Boolean(member.permissions?.momentsEnabled)
    });
    setShowPermissionsModal(true);
  };

  const savePermissions = async () => {
    if (!permissionsTarget) return;
    try {
      setPermissionsLoading(true);
      console.log('== DEBUG savePermissions ==');
      console.log('Target:', permissionsTarget?._id, permissionsTarget?.name);
      console.log('Payload:', {
        fichasEnabled: permissionsForm.fichasEnabled,
        eventsManageEnabled: Boolean(permissionsForm.eventsManageEnabled),
        momentsEnabled: Boolean(permissionsForm.momentsEnabled),
      });
      const res = await api.put(`/users/${permissionsTarget._id}/permissions`, {
        fichasEnabled: permissionsForm.fichasEnabled,
        eventsManageEnabled: Boolean(permissionsForm.eventsManageEnabled),
        momentsEnabled: Boolean(permissionsForm.momentsEnabled),
      });
      const updated = res.data.user;
      setMembers(prev => prev.map(m => (m._id === updated._id ? updated : m)));
      if (user?._id === updated._id) {
        try {
          updateUser(updated);
        } catch (e) {}
      }
      setShowPermissionsModal(false);
      setPermissionsTarget(null);
      toast.success('Permissões atualizadas com sucesso');
    } catch (error) {
      console.error('Erro ao salvar permissões:', error?.response?.data || error);
      const msg = error?.response?.data?.message || 'Falha ao salvar permissões';
      toast.error(msg);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      await api.delete(`/users/${memberToDelete._id}`);
      loadMembersData();
      setShowDeleteModal(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir membro:', error);
    }
  };

  const handleResendVerification = async (userId) => {
    try {
      setLoading(true);
      let response;
      try {
        response = await api.post(`/users/${userId}/resend-verification`);
      } catch (err) {
        const member = members.find(m => m._id === userId) || editingMember;
        if (member?.email) {
          const fallback = await api.post('/email/test-send', { email: member.email });
          response = { data: { user: { emailVerified: false }, message: fallback.data?.message || 'Convite enviado' } };
        } else {
          throw err;
        }
      }

      setMembers(prevMembers => 
        prevMembers.map(member => 
          member._id === userId 
            ? { ...member, emailVerified: response.data.user.emailVerified }
            : member
        )
      );
      if (editingMember && editingMember._id === userId) {
        setEditingMember(prev => ({ ...prev, emailVerified: response.data.user.emailVerified }));
      }
      toast.success(response.data?.message || 'Email de verificação enviado');
    } catch (error) {
      const msg = error?.response?.data?.message || 'Erro ao enviar email de verificação';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Funções para pré-cadastros
  const handleEditPreCadastro = (preCadastro) => {
    setEditingPreCadastro(preCadastro);
    setPreCadastroData({
      name: preCadastro.name,
      phone: preCadastro.phone,
      birthDate: preCadastro.birthDate
    });
    setShowEditPreCadastroModal(true);
  };

  const handleDeletePreCadastro = (preCadastro) => {
    setPreCadastroToDelete(preCadastro);
    setShowDeletePreCadastroModal(true);
  };

  const confirmDeletePreCadastro = async () => {
    if (!preCadastroToDelete) return;
    
    try {
      await api.delete(`/members/pre-cadastros/${preCadastroToDelete._id}`);
      setPreCadastros(prev => prev.filter(p => p._id !== preCadastroToDelete._id));
      setShowDeletePreCadastroModal(false);
      setPreCadastroToDelete(null);
    } catch (error) {
      console.error('Erro ao deletar pré-cadastro:', error);
      console.error('Erro ao deletar pré-cadastro. Tente novamente.');
    }
  };

  const saveEditPreCadastro = async () => {
    if (!editingPreCadastro) return;
    
    try {
      const response = await api.put(`/members/pre-cadastros/${editingPreCadastro._id}`, {
        name: preCadastroData.name,
        phone: preCadastroData.phone,
        birthDate: preCadastroData.birthDate
      });
      
      setPreCadastros(prev => prev.map(p => 
        p._id === editingPreCadastro._id ? response.data : p
      ));
      
      setShowEditPreCadastroModal(false);
      setEditingPreCadastro(null);
      setPreCadastroData({ name: '', phone: '', birthDate: '' });
    } catch (error) {
      console.error('Erro ao atualizar pré-cadastro:', error);
      console.error('Erro ao atualizar pré-cadastro. Tente novamente.');
    }
  };

  // Funções para visitantes
  const handleEditVisitante = (visitante) => {
    setEditingVisitante(visitante);
    setCadastroData({
      name: visitante.name,
      phone: visitante.phone,
      canSendMessage: visitante.canSendMessage,
      type: 'visitante',
      visitDate: visitante.visitDate,
      isReconciling: '',
      reconcilingDate: '',
      acceptedJesus: '',
      acceptedJesusDate: ''
    });
    setShowEditVisitanteModal(true);
  };

  const handleDeleteVisitante = (visitante) => {
    setVisitanteToDelete(visitante);
    setShowDeleteVisitanteModal(true);
  };

  const confirmDeleteVisitante = async () => {
    if (!visitanteToDelete) return;
    
    try {
      await api.delete(`/members/visitantes/${visitanteToDelete._id}`);
      
      // Recarregar lista de visitantes
      loadVisitantes();
      
      setShowDeleteVisitanteModal(false);
      setVisitanteToDelete(null);
      
      console.log('Visitante excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir visitante:', error);
      console.error('Erro ao excluir visitante. Tente novamente.');
    }
  };

  const saveEditVisitante = async () => {
    if (!editingVisitante) return;
    
    try {
      await api.put(`/members/visitantes/${editingVisitante._id}`, {
        name: cadastroData.name,
        phone: cadastroData.phone,
        canSendMessage: cadastroData.canSendMessage,
        visitDate: cadastroData.visitDate
      });
      
      // Recarregar lista de visitantes
      loadVisitantes();
      
      setShowEditVisitanteModal(false);
      setEditingVisitante(null);
      setCadastroData({
        name: '',
        canSendMessage: '',
        phone: '',
        type: '',
        visitDate: '',
        isReconciling: '',
        reconcilingDate: '',
        acceptedJesus: '',
        acceptedJesusDate: ''
      });
      
      // Feedback silencioso para evitar pop-up
      console.log('Visitante atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar visitante:', error);
      toast.error('Erro ao atualizar visitante. Tente novamente.');
    }
  };

  // Funções para decisões
  const handleEditDecisao = (decisao) => {
    setEditingDecisao(decisao);
    setCadastroData({
      name: decisao.name,
      phone: decisao.phone,
      canSendMessage: decisao.canSendMessage,
      type: 'decisao',
      visitDate: '', // Limpar visitDate para decisões
      isReconciling: decisao.isReconciling,
      reconcilingDate: decisao.reconcilingDate,
      acceptedJesus: decisao.acceptedJesus,
      acceptedJesusDate: decisao.acceptedJesusDate
    });
    setShowEditDecisaoModal(true);
  };

  const handleDeleteDecisao = (decisao) => {
    setDecisaoToDelete(decisao);
    setShowDeleteDecisaoModal(true);
  };

  const confirmDeleteDecisao = async () => {
    if (!decisaoToDelete) return;
    
    try {
      await api.delete(`/members/decisoes/${decisaoToDelete._id}`);
      
      // Recarregar lista de decisões
      loadDecisoes();
      
      setShowDeleteDecisaoModal(false);
      setDecisaoToDelete(null);
      
      console.log('Decisão excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir decisão:', error);
      console.error('Erro ao excluir decisão. Tente novamente.');
    }
  };

  const saveEditDecisao = async () => {
    if (!editingDecisao) return;
    
    try {
      await api.put(`/members/decisoes/${editingDecisao._id}`, {
        name: cadastroData.name,
        phone: cadastroData.phone,
        canSendMessage: cadastroData.canSendMessage,
        visitDate: cadastroData.visitDate,
        isReconciling: cadastroData.isReconciling,
        reconcilingDate: cadastroData.reconcilingDate,
        acceptedJesus: cadastroData.acceptedJesus,
        acceptedJesusDate: cadastroData.acceptedJesusDate
      });
      
      // Recarregar lista de decisões
      loadDecisoes();
      
      setShowEditDecisaoModal(false);
      setEditingDecisao(null);
      setCadastroData({
        name: '',
        canSendMessage: '',
        phone: '',
        type: '',
        visitDate: '',
        isReconciling: '',
        reconcilingDate: '',
        acceptedJesus: '',
        acceptedJesusDate: ''
      });
      
      console.log('Decisão atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar decisão:', error);
      toast.error('Erro ao atualizar decisão. Tente novamente.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      birthDate: '',
      role: 'membro',
      maritalStatus: '',
      gender: ''
    });
  };
  
  const resetCadastroForm = () => {
    setCadastroData({
      name: '',
      canSendMessage: '',
      phone: '',
      type: '',
      visitDate: '',
      isReconciling: '',
      reconcilingDate: '',
      acceptedJesus: '',
      acceptedJesusDate: ''
    });
  };
  
  const handleCadastroSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (cadastroData.type === 'visitante') {
        // Salvar visitante no backend
        const response = await api.post('/members/visitantes', {
          name: cadastroData.name,
          phone: cadastroData.phone,
          canSendMessage: cadastroData.canSendMessage,
          visitDate: cadastroData.visitDate
        });
        
        // Recarregar lista de visitantes
        loadVisitantes();
        
        console.log('Visitante salvo:', response.data);
        
      } else if (cadastroData.type === 'decisao') {
        // Validações obrigatórias para decisão
        if (!cadastroData.name?.trim()) {
          console.warn('Informe o nome.');
          return;
        }
        if (!cadastroData.canSendMessage) {
          console.warn('Selecione se podemos enviar mensagens.');
          return;
        }
        if (!cadastroData.isReconciling) {
          console.warn('Selecione se está se reconciliando.');
          return;
        }
        // Validação: se NÃO está se reconciliando, a resposta de aceitar Jesus é obrigatória
        if (cadastroData.isReconciling === 'não' && !cadastroData.acceptedJesus) {
          console.warn('Selecione se está aceitando a Jesus.');
          return;
        }

        // Montar payload conforme a lógica: só envia aceitação se NÃO está reconciliando
        const payload = {
          name: cadastroData.name,
          phone: cadastroData.phone,
          canSendMessage: cadastroData.canSendMessage,
          isReconciling: cadastroData.isReconciling,
          ...(cadastroData.isReconciling === 'sim' && cadastroData.reconcilingDate ? { reconcilingDate: cadastroData.reconcilingDate } : {}),
          acceptedJesus: cadastroData.isReconciling === 'sim' ? 'não' : cadastroData.acceptedJesus,
          ...(cadastroData.acceptedJesus === 'sim' && cadastroData.acceptedJesusDate ? { acceptedJesusDate: cadastroData.acceptedJesusDate } : {}),
        };

        const response = await api.post('/members/decisoes', payload);
        
        // Recarregar lista de decisões
        loadDecisoes();
        
        console.log('Decisão salva:', response.data);
      }
      
      // Fechar modal e resetar formulário
      setShowCadastroModal(false);
      resetCadastroForm();
      
      console.log('Cadastro realizado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao processar cadastro:', error);
      console.error('Erro ao salvar cadastro. Tente novamente.');
    }
  };
  
  const getCurrentDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    // Retorna data LOCAL no formato YYYY-MM-DD
    return `${yyyy}-${mm}-${dd}`;
  };

  const handlePreCadastroSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Salvar no backend
      const response = await api.post('/members/pre-cadastros', {
        name: preCadastroData.name,
        phone: preCadastroData.phone,
        birthDate: preCadastroData.birthDate
      });
      
      // Adicionar à lista de pré-cadastros
      setPreCadastros(prev => [...prev, response.data]);
      
      console.log('Pré-cadastro salvo:', response.data);
      
      // Fechar modal e resetar formulário
      setShowPreCadastroModal(false);
      setPreCadastroData({ name: '', phone: '', birthDate: '' });
      
      console.log('Pré-cadastro realizado com sucesso!');
      
    } catch (error) {
      console.error('Erro ao processar pré-cadastro:', error);
      console.error('Erro ao salvar pré-cadastro. Tente novamente.');
    }
  };

  const canEdit = user?.role === 'secretario' || user?.role === 'administracao';
  const [showCultoModal, setShowCultoModal] = useState(false);
  const [isOpeningCultoModal, setIsOpeningCultoModal] = useState(false);
  const [cultoOpenAnimate, setCultoOpenAnimate] = useState(false);
  const cultoDateRef = useRef(null);
  const [savingCulto, setSavingCulto] = useState(false);
  const [momentOptions, setMomentOptions] = useState(['Culto','Consagração']);
  const [showMomentModal, setShowMomentModal] = useState(false);
  const [savingMomentType, setSavingMomentType] = useState(false);
  const [momentForm, setMomentForm] = useState({ name: '', description: '' });
  const [showCultoPreview, setShowCultoPreview] = useState(false);
  const [cultoErrorFields, setCultoErrorFields] = useState(new Set());
  const [cultos, setCultos] = useState([]);
  const [cultosPage, setCultosPage] = useState(1);
  const CULTOS_LIMIT = 5;
  const [showMomentsListModal, setShowMomentsListModal] = useState(false);
  const [momentsFilter, setMomentsFilter] = useState('todos');
  const [momentsSearch, setMomentsSearch] = useState('');
  const [momentsListLoading, setMomentsListLoading] = useState(false);
  const [momentsListError, setMomentsListError] = useState('');
  const [momentsPage, setMomentsPage] = useState(1);
  const MOMENTS_LIMIT = 10;
  const [editingCultoKey, setEditingCultoKey] = useState(null);
  const [showCultoDeleteModal, setShowCultoDeleteModal] = useState(false);
  const [cultoToDelete, setCultoToDelete] = useState(null);
  const [cultoForm, setCultoForm] = useState({
    date: '',
    time: '19:00',
    moment: 'Culto',
    presentTotal: 0,
    absentTotal: 0,
    visitorsCount: 0,
    notes: ''
  });
  const validateCulto = (form) => {
    const errors = [];
    if (!String(form.date || '').trim()) errors.push('date');
    if (!String(form.moment || '').trim()) errors.push('moment');
    const nums = ['presentTotal','absentTotal','visitorsCount'];
    nums.forEach(k => {
      const v = Number(form[k]);
      if (isNaN(v) || v < 0) errors.push(k);
    });
    return errors;
  };
  const performSaveCulto = async () => {
    try {
      setSavingCulto(true);
      const payload = {
        title: 'Culto',
        date: cultoForm.date,
        time: cultoForm.time,
        type: 'culto',
        presentTotal: Number(cultoForm.presentTotal) || 0,
        absentTotal: Number(cultoForm.absentTotal) || 0,
        visitorsCount: Number(cultoForm.visitorsCount) || 0,
        notes: cultoForm.notes ? `${cultoForm.moment}: ${cultoForm.notes}` : cultoForm.moment
      };
      console.log('== DEBUG performSaveCulto payload ==', payload);
      if (editingCultoKey) {
        try {
          const id = editingCultoKey.startsWith('id:') ? editingCultoKey.slice(3) : null;
          if (id) {
          const resp = await api.put(`/events/${id}`, payload);
            console.log('== DEBUG update event response ==', resp.status, resp.data);
          }
        } catch (e) {
          console.error('Falha ao atualizar evento:', e?.response?.data || e);
        }
        setCultos(prev => {
          const next = prev.map(r => {
            const key = r._id ? `id:${r._id}` : (r.createdAt ? `ts:${r.createdAt}` : '');
            return key === editingCultoKey ? { ...r, ...payload } : r;
          });
          try { localStorage.setItem('cultosRecords', JSON.stringify(next)); } catch {}
          return next;
        });
        toast.success('Culto atualizado');
      } else {
        try {
          const resp = await api.post('/events', payload);
          console.log('== DEBUG create event response ==', resp.status, resp.data);
          toast.success('Culto registrado');
        } catch (e) {
          console.error('Falha ao registrar culto no backend:', e?.response?.data || e);
          toast.info('Culto registrado localmente (offline)');
        }
        const record = { ...payload, createdAt: new Date().toISOString(), isNew: true };
        setCultos(prev => {
          const next = [record, ...prev];
          try { localStorage.setItem('cultosRecords', JSON.stringify(next)); } catch {}
          return next;
        });
      }
      setTimeout(() => {
        setCultos(prev => prev.map(r => ({ ...r, isNew: false })));
      }, 2000);
      setShowCultoPreview(false);
      setShowCultoModal(false);
      setEditingCultoKey(null);
      setCultoForm({ date: '', time: '19:00', moment: 'Culto', presentTotal: 0, absentTotal: 0, visitorsCount: 0, notes: '' });
    } catch {
      toast.error('Falha ao registrar culto');
    } finally {
      setSavingCulto(false);
    }
  };
  const handleConfirmCulto = async () => {
    const errs = validateCulto(cultoForm);
    if (errs.length) {
      setCultoErrorFields(new Set(errs));
      setShowCultoPreview(false);
      toast.error('Corrija os campos destacados');
      return;
    }
    setCultoErrorFields(new Set());
    await performSaveCulto();
  };
  const isPositiveInt = (x) => /^\d+$/.test(String(x));
  const handlePreviewCountChange = (key, raw) => {
    const vStr = String(raw);
    const cleaned = vStr.replace(/[^0-9]/g, '');
    const n = cleaned === '' ? '' : Number(cleaned);
    setCultoForm(prev => ({ ...prev, [key]: n === '' ? 0 : n }));
    setCultoErrorFields(prev => {
      const next = new Set(prev);
      if (cleaned === '' || !isPositiveInt(cleaned)) next.add(key); else next.delete(key);
      return next;
    });
  };
  const formatBRDate = (val) => {
    if (!val) return '-';
    const s = String(val);
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) {
      const [yyyy, mm, dd] = m[1].split('-');
      return `${dd}/${mm}/${yyyy}`;
    }
    try {
      const d = new Date(s);
      if (isNaN(d)) return s;
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = d.getUTCFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return s; }
  };
  const momentPeriod = (time) => {
    const h = Number(String(time || '00:00').split(':')[0] || 0);
    if (h < 12) return 'manhã';
    if (h < 18) return 'tarde';
    return 'noite';
  };
  const eventMomentLabel = (c) => {
    try {
      const t = String(c?.type || '').trim().toLowerCase();
      if (t) return t;
      return momentPeriod(c?.time);
    } catch {
      return 'culto';
    }
  };
  const sortByDateDesc = (arr) => {
    return (arr || []).slice().sort((a,b) => new Date(b.date || b.createdAt || 0) - new Date(a.date || a.createdAt || 0));
  };
  const cultosPaginated = useMemo(() => {
    return sortByDateDesc(cultos).slice((cultosPage - 1) * CULTOS_LIMIT, cultosPage * CULTOS_LIMIT);
  }, [cultos, cultosPage]);
  const momentsFiltered = useMemo(() => {
    const base = sortByDateDesc(cultos);
    const byFilter = momentsFilter === 'todos' ? base : base.filter(c => {
      const label = String(eventMomentLabel(c)).toLowerCase();
      return label.includes(momentsFilter);
    });
    if (!momentsSearch) return byFilter;
    const q = momentsSearch.toLowerCase();
    return byFilter.filter(c => {
      const text = `${formatBRDate(c.date || c.createdAt)} ${eventMomentLabel(c)} ${c.notes || ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [cultos, momentsFilter, momentsSearch]);
  const momentsPaginated = useMemo(() => {
    return momentsFiltered.slice((momentsPage - 1) * MOMENTS_LIMIT, momentsPage * MOMENTS_LIMIT);
  }, [momentsFiltered, momentsPage]);
  const loadMomentsList = async () => {
    try {
      setMomentsListLoading(true);
      setMomentsListError('');
      await loadCultos();
    } catch {
      setMomentsListError('Erro ao carregar momentos');
    } finally {
      setMomentsListLoading(false);
    }
  };
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const tabParam = params.get('tab');
      const listParam = params.get('list');
      if (tabParam === 'activities') setActiveTab('activities');
      if (listParam === 'moments') {
        setShowMomentsListModal(true);
        loadMomentsList();
      }
    } catch {}
  }, [location.search]);
  const loadCultos = async () => {
    try {
      const res = await api.get('/events', { params: { type: 'culto' } });
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.events) ? res.data.events : []);
      setCultos(sortByDateDesc(list));
      try { localStorage.setItem('cultosRecords', JSON.stringify(list)); } catch {}
    } catch {
      try {
        const ls = localStorage.getItem('cultosRecords');
        const parsed = ls ? JSON.parse(ls) : [];
        setCultos(sortByDateDesc(parsed));
      } catch {
        setCultos([]);
      }
    }
  };
  useEffect(() => {
    if (activeTab === 'activities') loadCultos();
  }, [activeTab]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/moment-types');
        const names = (res.data || []).map(x => x.name);
        if (names.length) setMomentOptions(names);
      } catch {}
    })();
  }, []);
  useEffect(() => {
    if (showMomentModal) {
      try {
        const draft = localStorage.getItem('momentFormDraft');
        if (draft) {
          const obj = JSON.parse(draft);
          if (obj && typeof obj === 'object') setMomentForm({ name: obj.name || '', description: obj.description || '' });
        }
      } catch {}
    }
  }, [showMomentModal]);
  useEffect(() => {
    if (showMomentModal) {
      try { localStorage.setItem('momentFormDraft', JSON.stringify(momentForm)); } catch {}
    }
  }, [momentForm, showMomentModal]);
  const submitMomentType = async (e) => {
    e.preventDefault();
    const n = (momentForm.name || '').trim();
    if (!n) { toast.error('Informe um nome'); return; }
    if (momentOptions.includes(n)) { toast.error('Momento já existe'); return; }
    try {
      setSavingMomentType(true);
      try {
        const res = await api.post('/moment-types', { name: n, description: momentForm.description });
        const created = res.data;
        setMomentOptions(prev => [...prev.filter(x => x !== n), created.name]);
        setCultoForm(prev => ({ ...prev, moment: created.name }));
        toast.success('Momento adicionado e salvo');
      } catch (err) {
        if (err?.response?.status === 409) {
          toast.info('Momento já existe no banco');
          setMomentOptions(prev => [...prev, n]);
          setCultoForm(prev => ({ ...prev, moment: n }));
        } else {
          toast.error('Falha ao salvar no banco, usando local');
          setMomentOptions(prev => [...prev, n]);
          setCultoForm(prev => ({ ...prev, moment: n }));
        }
      }
      try { localStorage.removeItem('momentFormDraft'); } catch {}
      setShowMomentModal(false);
      setMomentForm({ name: '', description: '' });
    } catch {
      toast.error('Falha ao adicionar momento');
    } finally {
      setSavingMomentType(false);
    }
  };
  const handleEditCulto = (c) => {
    try {
      setCultoForm({
        date: c.date || (c.createdAt ? String(c.createdAt).slice(0,10) : ''),
        time: c.time || '19:00',
        moment: c.type || 'Culto',
        presentTotal: Number(c.presentTotal) || 0,
        absentTotal: Number(c.absentTotal) || 0,
        visitorsCount: Number(c.visitorsCount) || 0,
        notes: c.notes || ''
      });
      const key = c._id ? `id:${c._id}` : (c.createdAt ? `ts:${c.createdAt}` : '');
      setEditingCultoKey(key || null);
      setShowCultoModal(true);
    } catch {}
  };
  const confirmDeleteCulto = async (c) => {
    try {
      if (c._id) {
        try { await api.delete(`/events/${c._id}`); } catch {}
      }
      setCultos(prev => {
        const next = prev.filter(r => {
          const key = r._id ? `id:${r._id}` : (r.createdAt ? `ts:${r.createdAt}` : '');
          const target = c._id ? `id:${c._id}` : (c.createdAt ? `ts:${c.createdAt}` : '');
          return key !== target;
        });
        try { localStorage.setItem('cultosRecords', JSON.stringify(next)); } catch {}
        return next;
      });
      toast.success('Culto excluído');
    } catch {
      toast.error('Falha ao excluir culto');
    }
  };
  const handleDeleteCulto = (c) => {
    setCultoToDelete(c);
    setShowCultoDeleteModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #60a5fa 100%)',
        borderRadius: '16px',
        padding: '24px',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '-60px', right: '-40px' }} />
        <div style={{ position: 'absolute', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: '-30px', right: '80px' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, backdropFilter: 'blur(4px)',
              border: '2px solid rgba(255,255,255,0.3)',
            }}>
              <Users size={24} color="#fff" />
            </div>
            <div>
              <p style={{ fontSize: '13px', opacity: '0.8', margin: '0 0 2px' }}>Gestão de membros</p>
              <h1 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 4px', lineHeight: '1.2' }}>Secretaria</h1>
              <span style={{
                display: 'inline-block', fontSize: '11px', fontWeight: '600',
                background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
                padding: '2px 10px', backdropFilter: 'blur(4px)',
              }}>
                {stats?.total || pagination.total} {(stats?.total || pagination.total) === 1 ? 'membro' : 'membros'}
              </span>
            </div>
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => { resetForm(); setEditingMember(null); setShowModal(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                  background: 'rgba(255,255,255,0.95)', color: '#1d4ed8',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <Plus size={16} />
                <span>Novo Membro</span>
              </button>
              <button
                onClick={() => { resetCadastroForm(); setShowCadastroModal(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.4)', cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <UserCheck size={16} />
                <span>Fichas</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ações secundárias */}
      {canEdit && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => { setPreCadastroData({ name: '', phone: '', birthDate: '' }); setShowPreCadastroModal(true); }}
            className="btn btn-outline"
          >
            <UserPlus className="w-4 h-4" />
            <span>Pré-cadastro</span>
          </button>
          <button
            onClick={() => setShowReportModal(true)}
            className="btn btn-outline"
          >
            <FileText className="w-4 h-4" />
            <span>Relatórios</span>
          </button>
        </div>
      )}

      {/* Navigation Tabs */}
      <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '4px', display: 'flex', gap: '4px', overflowX: 'auto' }}>
        {[
          { id: 'members', label: 'Membros', icon: Users },
          { id: 'visitors', label: 'Integra', icon: UserCheck },
          { id: 'celulas', label: 'Células', icon: Settings },
          { id: 'statistics', label: 'Estatísticas', icon: BarChart3 },
          { id: 'activities', label: 'Cultos', icon: Clock },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '8px 12px', borderRadius: '9px', fontSize: '14px', fontWeight: '500',
              border: 'none', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap', minWidth: 0,
              background: activeTab === id ? '#ffffff' : 'transparent',
              color: activeTab === id ? '#1d4ed8' : '#64748b',
              boxShadow: activeTab === id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Conteúdo baseado na aba ativa */}
      {activeTab === 'celulas' && (
        <div className="space-y-6">
          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Células</h3>
             <p className="text-gray-600">Seção de células em desenvolvimento.</p>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={showCultoModal}
        onClose={() => setShowCultoModal(false)}
        title="Adicionar Culto"
        size="lg"
        placement="center"
      >
        <div style={{ transition: 'opacity 200ms ease, transform 200ms ease', opacity: cultoOpenAnimate ? 0 : 1, transform: cultoOpenAnimate ? 'translateY(8px) scale(0.98)' : 'translateY(0) scale(1)' }}>
        <form onSubmit={(e)=>{ e.preventDefault(); setShowCultoPreview(true); }} aria-label="Formulário de culto">
          <div style={{ display:'flex', flexDirection:'column', gap:'18px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              <div>
                <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data</label>
                <input
                  type="date"
                  ref={cultoDateRef}
                  value={cultoForm.date}
                  max={getTodayLocalISO()}
                  onChange={(e)=>{
                    const val = e.target.value;
                    if (isFutureISO(val)) {
                      toast.error('Data futura não é permitida');
                      setCultoForm(prev => ({ ...prev, date: getTodayLocalISO() }));
                    } else {
                      setCultoForm(prev => ({ ...prev, date: val }));
                    }
                  }}
                  required
                  style={{ padding:'10px 12px', borderRadius:'10px', border:`1.5px solid ${cultoErrorFields.has('date') ? '#ef4444' : '#e2e8f0'}`, fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Horário</label>
                <input type="time" value={cultoForm.time} onChange={(e)=>setCultoForm({...cultoForm, time:e.target.value})}
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
            </div>

            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Momento</label>
              <div style={{ display:'flex', gap:'8px' }}>
                <select value={cultoForm.moment} onChange={(e)=>setCultoForm({...cultoForm, moment:e.target.value})}
                  aria-label="Selecionar momento"
                  style={{ padding:'10px 12px', borderRadius:'10px', border:`1.5px solid ${cultoErrorFields.has('moment') ? '#ef4444' : '#e2e8f0'}`, fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', flex:1, boxSizing:'border-box', cursor:'pointer' }}>
                  {momentOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <button type="button" aria-label="Adicionar momento" onClick={() => setShowMomentModal(true)}
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', background:'#f8fafc', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b', flexShrink:0 }}>
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px' }}>
              {[
                { label:'Adultos', key:'presentTotal', color:'#3b82f6', bg:'#eff6ff' },
                { label:'Crianças', key:'absentTotal', color:'#8b5cf6', bg:'#f5f3ff' },
                { label:'Visitantes', key:'visitorsCount', color:'#10b981', bg:'#f0fdf4' },
              ].map(({ label, key, color, bg }) => (
                <div key={key} style={{ background:bg, borderRadius:'12px', padding:'12px', textAlign:'center', border:`1.5px solid ${color}20` }}>
                  <label style={{ fontSize:'11px', fontWeight:'700', color, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'6px' }}>{label}</label>
                  <input type="number" min="0" value={cultoForm[key]}
                    onChange={(e)=>setCultoForm({...cultoForm, [key]:e.target.value})}
                    style={{ width:'100%', padding:'6px 8px', borderRadius:'8px', border:`1.5px solid ${cultoErrorFields.has(key) ? '#ef4444' : color+'40'}`, fontSize:'20px', fontWeight:'700', color, background:'#fff', outline:'none', textAlign:'center', boxSizing:'border-box' }} />
                </div>
              ))}
            </div>

            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>
                Observações <span style={{ fontWeight:'400', textTransform:'none', color:'#94a3b8' }}>(opcional)</span>
              </label>
              <textarea value={cultoForm.notes} onChange={(e)=>setCultoForm({...cultoForm, notes:e.target.value})} rows={3}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box', resize:'vertical' }} />
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px', paddingTop:'16px', borderTop:'1px solid #f1f5f9' }}>
            <button type="button" onClick={()=>setShowCultoModal(false)} style={{ padding:'10px 20px', borderRadius:'10px', border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontSize:'14px', fontWeight:'600', cursor:'pointer' }}>Cancelar</button>
            <button type="submit" disabled={savingCulto} aria-busy={savingCulto ? 'true' : 'false'}
              style={{ padding:'10px 24px', borderRadius:'10px', border:'none', background:'#3b82f6', color:'#fff', fontSize:'14px', fontWeight:'700', cursor: savingCulto ? 'not-allowed' : 'pointer', opacity: savingCulto ? 0.7 : 1, display:'flex', alignItems:'center', gap:'6px' }}>
              {savingCulto ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</> : 'Registrar Culto'}
            </button>
          </div>
        </form>
        </div>
      </Modal>
      <ConfirmModal
        isOpen={showCultoDeleteModal}
        onClose={() => {
          setShowCultoDeleteModal(false);
          setCultoToDelete(null);
        }}
        onConfirm={() => {
          if (cultoToDelete) confirmDeleteCulto(cultoToDelete);
        }}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir este item?"
        confirmText="Confirmar"
        cancelText="Cancelar"
        variant="danger"
      />
      <Modal
        isOpen={showCultoPreview}
        onClose={() => setShowCultoPreview(false)}
        title="Pré-visualização do Culto"
        size="md"
        placement="center"
      >
        <div className="space-y-6 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><strong>Data:</strong> {cultoForm.date || '-'}</div>
            <div><strong>Horário:</strong> {cultoForm.time || '-'}</div>
            <div><strong>Momento:</strong> {cultoForm.moment || '-'}</div>
          </div>
          <div className="mt-6">
            <table className="w-full table-auto text-sm border rounded culto-preview-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left px-3 py-2">Adultos</th>
                  <th className="text-left px-3 py-2">Crianças</th>
                  <th className="text-left px-3 py-2">Visitantes</th>
                  <th className="text-left px-3 py-2">Total de Presentes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      className={`form-input ${cultoErrorFields.has('presentTotal') ? 'border-red-500 bg-red-50' : ''}`}
                      aria-label="Adultos"
                      value={String(cultoForm.presentTotal || 0)}
                      onChange={(e) => handlePreviewCountChange('presentTotal', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      className={`form-input ${cultoErrorFields.has('absentTotal') ? 'border-red-500 bg-red-50' : ''}`}
                      aria-label="Crianças"
                      value={String(cultoForm.absentTotal || 0)}
                      onChange={(e) => handlePreviewCountChange('absentTotal', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      inputMode="numeric"
                      className={`form-input ${cultoErrorFields.has('visitorsCount') ? 'border-red-500 bg-red-50' : ''}`}
                      aria-label="Visitantes"
                      value={String(cultoForm.visitorsCount || 0)}
                      onChange={(e) => handlePreviewCountChange('visitorsCount', e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">
                      {(Number(cultoForm.presentTotal)||0) + (Number(cultoForm.absentTotal)||0) + (Number(cultoForm.visitorsCount)||0)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <strong>Observações:</strong>
            <div className="mt-1 p-2 border rounded bg-background-color">{cultoForm.notes || '-'}</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn btn-outline" onClick={()=>setShowCultoPreview(false)}>Editar</button>
          <button type="button" className={`btn btn-primary ${savingCulto ? 'opacity-75 cursor-not-allowed' : ''}`} onClick={handleConfirmCulto} aria-busy={savingCulto ? 'true' : 'false'}>
            {savingCulto ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={showMomentModal}
        onClose={() => setShowMomentModal(false)}
        title="Adicionar Momento"
        size="md"
        placement="center"
      >
        <form onSubmit={submitMomentType} aria-label="Formulário de momento">
          <div className="grid grid-cols-1 gap-3">
            <div className="form-group">
              <label className="form-label">Nome</label>
              <input className="form-input" value={momentForm.name} onChange={(e)=>setMomentForm({...momentForm, name:e.target.value})} required />
            </div>
            <div className="form-group">
              <label className="form-label">Descrição</label>
              <textarea className="form-input" value={momentForm.description} onChange={(e)=>setMomentForm({...momentForm, description:e.target.value})} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="btn btn-outline" onClick={()=>setShowMomentModal(false)}>Cancelar</button>
            <button type="submit" className={`btn btn-primary ${savingMomentType ? 'opacity-75 cursor-not-allowed' : ''}`} aria-busy={savingMomentType ? 'true' : 'false'}>
              {savingMomentType ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
      
      {activeTab === 'visitors' && (
        <div className="space-y-6">
          {/* Lista de Pré-cadastro */}
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #f0e6ff', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #7c3aed, #9333ea)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <UserPlus size={20} color="#fff" />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fff' }}>Pré-cadastros</p>
                  <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>Aguardando efetivação</p>
                </div>
              </div>
              {preCadastros.length > 0 && (
                <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '13px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' }}>{preCadastros.length}</span>
              )}
            </div>
            <div style={{ padding: '16px 20px' }}>
              {preCadastros.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                  <UserPlus size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: '0 0 4px', fontWeight: '500' }}>Nenhum pré-cadastro realizado</p>
                  <p style={{ margin: 0, fontSize: '13px' }}>Use o botão "Pré-cadastro" para adicionar novos registros</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {preCadastros.map((preCadastro) => {
                    const initials = preCadastro.name.split(' ').filter(Boolean).slice(0,2).map(n => n[0].toUpperCase()).join('');
                    return (
                      <div key={preCadastro._id} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '12px', border: '1px solid #f3e8ff', borderLeft: '4px solid #9333ea', background: '#faf5ff', transition: 'box-shadow 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 8px rgba(147,51,234,0.12)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                        <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ color: '#fff', fontSize: '14px', fontWeight: '700' }}>{initials}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '14px', color: '#1e1b4b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{preCadastro.name}</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                            {preCadastro.phone && <span style={{ fontSize: '12px', color: '#6b7280' }}>{preCadastro.phone}</span>}
                            {preCadastro.quality && <span style={{ fontSize: '11px', fontWeight: '600', background: '#ede9fe', color: '#6d28d9', padding: '2px 8px', borderRadius: '10px' }}>{preCadastro.quality}</span>}
                            {preCadastro.birthDate && <span style={{ fontSize: '11px', color: '#9ca3af' }}>Nasc. {formatDateSafe(toInputDateValue(preCadastro.birthDate))}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                          <span style={{ fontSize: '11px', color: '#a78bfa', fontWeight: '500' }}>{new Date(preCadastro.createdAt).toLocaleDateString('pt-BR')}</span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => handleEditPreCadastro(preCadastro)} title="Editar"
                              style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #ddd6fe', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#7c3aed' }}>
                              <Edit size={13} />
                            </button>
                            <button onClick={() => handleDeletePreCadastro(preCadastro)} title="Excluir"
                              style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #fecdd3', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de Visitantes */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #dcfce7', overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg, #15803d, #22c55e)', padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: visitantes.length > 0 ? '14px' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <UserCheck size={20} color="#fff" />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fff' }}>Visitantes</p>
                      <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>Acompanhamento de visitas</p>
                    </div>
                  </div>
                  {visitantes.length > 0 && (
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '13px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' }}>{visitantes.length}</span>
                  )}
                </div>
                {visitantes.length > 0 && (() => {
                  const mesesNomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                  const anos = [...new Set(visitantes.map(v => v.visitDate ? new Date(v.visitDate).getFullYear() : null).filter(Boolean))].sort((a,b) => b-a);
                  const contMeses = {};
                  visitantes
                    .filter(v => v.visitDate && (filtroAnoVisitante === 'todos' || new Date(v.visitDate).getFullYear() === parseInt(filtroAnoVisitante)))
                    .forEach(v => { const m = new Date(v.visitDate).getMonth(); contMeses[m] = (contMeses[m] || 0) + 1; });
                  const mesesDisponiveis = Object.keys(contMeses).map(Number).sort((a,b) => a-b);
                  const bA = { fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '12px', border: 'none', cursor: 'pointer' };
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        <button onClick={() => { setFiltroAnoVisitante('todos'); setFiltroMesVisitante('todos'); }} style={{ ...bA, background: filtroAnoVisitante === 'todos' ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroAnoVisitante === 'todos' ? '#15803d' : '#fff' }}>Todos os anos</button>
                        {anos.map(ano => (
                          <button key={ano} onClick={() => { setFiltroAnoVisitante(String(ano)); setFiltroMesVisitante('todos'); }} style={{ ...bA, background: filtroAnoVisitante === String(ano) ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroAnoVisitante === String(ano) ? '#15803d' : '#fff' }}>{ano}</button>
                        ))}
                      </div>
                      {mesesDisponiveis.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                          <button onClick={() => setFiltroMesVisitante('todos')} style={{ ...bA, background: filtroMesVisitante === 'todos' ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroMesVisitante === 'todos' ? '#15803d' : '#fff' }}>Todos</button>
                          {mesesDisponiveis.map(m => (
                            <button key={m} onClick={() => setFiltroMesVisitante(String(m))} style={{ ...bA, background: filtroMesVisitante === String(m) ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroMesVisitante === String(m) ? '#15803d' : '#fff' }}>
                              {mesesNomes[m]} ({contMeses[m]})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ padding: '16px 20px' }}>
                {(() => {
                  const visitantesFiltrados = visitantes.filter(v => {
                    if (!v.visitDate) return filtroAnoVisitante === 'todos' && filtroMesVisitante === 'todos';
                    const d = new Date(v.visitDate);
                    if (filtroAnoVisitante !== 'todos' && d.getFullYear() !== parseInt(filtroAnoVisitante)) return false;
                    if (filtroMesVisitante !== 'todos' && d.getMonth() !== parseInt(filtroMesVisitante)) return false;
                    return true;
                  });
                  if (visitantesFiltrados.length === 0) return (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      <UserCheck size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                      <p style={{ margin: '0 0 4px', fontWeight: '500' }}>{visitantes.length === 0 ? 'Nenhum visitante cadastrado' : 'Nenhum visitante no período'}</p>
                      {visitantes.length === 0 && <p style={{ margin: 0, fontSize: '13px' }}>Use o formulário para adicionar visitantes</p>}
                    </div>
                  );
                  return (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{visitantesFiltrados.length} visitante{visitantesFiltrados.length !== 1 ? 's' : ''}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {visitantesFiltrados.map((visitante) => {
                          const initials = visitante.name.split(' ').filter(Boolean).slice(0,2).map(n => n[0].toUpperCase()).join('');
                          return (
                            <div key={visitante._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', border: '1px solid #dcfce7', borderLeft: '4px solid #22c55e', background: '#f0fdf4', transition: 'box-shadow 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 8px rgba(34,197,94,0.12)'}
                              onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #15803d, #22c55e)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>{initials}</span>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: '0 0 3px', fontWeight: '600', fontSize: '14px', color: '#14532d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{visitante.name}</p>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  {visitante.phone && <span style={{ fontSize: '12px', color: '#6b7280' }}>{visitante.phone}</span>}
                                  {visitante.visitDate && <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '500' }}>Visitou {formatDateSafe(toInputDateValue(visitante.visitDate))}</span>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <button onClick={() => handleEditVisitante(visitante)} title="Editar" style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #bbf7d0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#15803d' }}><Edit size={13} /></button>
                                <button onClick={() => handleDeleteVisitante(visitante)} title="Excluir" style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #fecdd3', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={13} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Decisões */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #dbeafe', overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg, #1d4ed8, #3b82f6)', padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FileText size={20} color="#fff" />
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#fff' }}>Decisões</p>
                      <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>Reconciliações e aceitações</p>
                    </div>
                  </div>
                  {decisoes.length > 0 && (
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '13px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' }}>{decisoes.length}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                  {[['todas','Todas'],['reconciliacao','Reconciliação'],['aceitacao','Aceitação']].map(([val, label]) => (
                    <button key={val} onClick={() => setFiltroDecisao(val)}
                      style={{ fontSize: '11px', fontWeight: '600', padding: '4px 12px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: filtroDecisao === val ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroDecisao === val ? '#1d4ed8' : '#fff' }}>
                      {label}
                    </button>
                  ))}
                </div>
                {decisoes.length > 0 && (() => {
                  const mesesNomes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
                  const anos = [...new Set(decisoes.map(d => d.createdAt ? new Date(d.createdAt).getFullYear() : null).filter(Boolean))].sort((a,b) => b-a);
                  const contMeses = {};
                  decisoes
                    .filter(d => d.createdAt && (filtroAnoDecisao === 'todos' || new Date(d.createdAt).getFullYear() === parseInt(filtroAnoDecisao)))
                    .forEach(d => { const m = new Date(d.createdAt).getMonth(); contMeses[m] = (contMeses[m] || 0) + 1; });
                  const mesesDisponiveis = Object.keys(contMeses).map(Number).sort((a,b) => a-b);
                  const bD = { fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '12px', border: 'none', cursor: 'pointer' };
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        <button onClick={() => { setFiltroAnoDecisao('todos'); setFiltroMesDecisao('todos'); }} style={{ ...bD, background: filtroAnoDecisao === 'todos' ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroAnoDecisao === 'todos' ? '#1d4ed8' : '#fff' }}>Todos os anos</button>
                        {anos.map(ano => (
                          <button key={ano} onClick={() => { setFiltroAnoDecisao(String(ano)); setFiltroMesDecisao('todos'); }} style={{ ...bD, background: filtroAnoDecisao === String(ano) ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroAnoDecisao === String(ano) ? '#1d4ed8' : '#fff' }}>{ano}</button>
                        ))}
                      </div>
                      {mesesDisponiveis.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                          <button onClick={() => setFiltroMesDecisao('todos')} style={{ ...bD, background: filtroMesDecisao === 'todos' ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroMesDecisao === 'todos' ? '#1d4ed8' : '#fff' }}>Todos</button>
                          {mesesDisponiveis.map(m => (
                            <button key={m} onClick={() => setFiltroMesDecisao(String(m))} style={{ ...bD, background: filtroMesDecisao === String(m) ? '#fff' : 'rgba(255,255,255,0.15)', color: filtroMesDecisao === String(m) ? '#1d4ed8' : '#fff' }}>
                              {mesesNomes[m]} ({contMeses[m]})
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div style={{ padding: '16px 20px' }}>
                {(() => {
                  const decisoesFiltradas = decisoes.filter(decisao => {
                    if (filtroDecisao === 'reconciliacao' && decisao.isReconciling !== 'sim') return false;
                    if (filtroDecisao === 'aceitacao' && decisao.acceptedJesus !== 'sim') return false;
                    if (decisao.createdAt) {
                      const d = new Date(decisao.createdAt);
                      if (filtroAnoDecisao !== 'todos' && d.getFullYear() !== parseInt(filtroAnoDecisao)) return false;
                      if (filtroMesDecisao !== 'todos' && d.getMonth() !== parseInt(filtroMesDecisao)) return false;
                    }
                    return true;
                  });
                  if (decisoesFiltradas.length === 0) return (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      <FileText size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                      <p style={{ margin: '0 0 4px', fontWeight: '500' }}>{decisoes.length === 0 ? 'Nenhuma decisão registrada' : 'Nenhuma decisão no período'}</p>
                      {decisoes.length === 0 && <p style={{ margin: 0, fontSize: '13px' }}>Use o formulário para adicionar decisões</p>}
                    </div>
                  );
                  return (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>{decisoesFiltradas.length} decisão{decisoesFiltradas.length !== 1 ? 'ões' : ''}</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {decisoesFiltradas.map((decisao) => {
                          const initials = decisao.name.split(' ').filter(Boolean).slice(0,2).map(n => n[0].toUpperCase()).join('');
                          const isAceit = decisao.acceptedJesus === 'sim';
                          return (
                            <div key={decisao._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '12px', border: '1px solid #dbeafe', borderLeft: `4px solid ${isAceit ? '#f59e0b' : '#3b82f6'}`, background: isAceit ? '#fffbeb' : '#eff6ff', transition: 'box-shadow 0.15s' }}
                              onMouseEnter={e => e.currentTarget.style.boxShadow='0 2px 8px rgba(59,130,246,0.12)'}
                              onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: isAceit ? 'linear-gradient(135deg, #d97706, #f59e0b)' : 'linear-gradient(135deg, #1d4ed8, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ color: '#fff', fontSize: '13px', fontWeight: '700' }}>{initials}</span>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '14px', color: '#1e3a5f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{decisao.name}</p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  {decisao.phone && <span style={{ fontSize: '12px', color: '#6b7280' }}>{decisao.phone}</span>}
                                  {decisao.isReconciling === 'sim' && <span style={{ fontSize: '11px', fontWeight: '600', background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '10px' }}>Reconciliação{decisao.reconcilingDate ? ` • ${formatDateSafe(toInputDateValue(decisao.reconcilingDate))}` : ''}</span>}
                                  {isAceit && <span style={{ fontSize: '11px', fontWeight: '600', background: '#fef3c7', color: '#b45309', padding: '2px 8px', borderRadius: '10px' }}>Aceitou Jesus{decisao.acceptedJesusDate ? ` • ${formatDateSafe(toInputDateValue(decisao.acceptedJesusDate))}` : ''}</span>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                <button onClick={() => handleEditDecisao(decisao)} title="Editar" style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #bfdbfe', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#1d4ed8' }}><Edit size={13} /></button>
                                <button onClick={() => handleDeleteDecisao(decisao)} title="Excluir" style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #fecdd3', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={13} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'members' && (
        <>
          {/* Estatísticas Resumidas */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-secondary">Membros</p>
                      <p className="text-lg md:text-xl font-bold text-primary">
                        {stats.total || 0}
                      </p>
                    </div>
                    <Users className="w-5 h-5 md:w-6 md:h-6 text-primary-color" />
                  </div>
                </div>
              </div>



              <div className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-secondary">Novos</p>
                      <p className="text-lg md:text-xl font-bold text-success">
                        {stats.recentMembers || 0}
                      </p>
                    </div>
                    <UserCheck className="w-5 h-5 md:w-6 md:h-6 text-success-color" />
                  </div>
                </div>
              </div>



              <div className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs md:text-sm text-secondary">Líderes de Célula</p>
                      <p className="text-lg md:text-xl font-bold text-primary">
                        {stats?.celulaLeaders || 0}
                      </p>
                    </div>
                    <UserCheck className="w-5 h-5 md:w-6 md:h-6 text-warning-color" />
                  </div>
                </div>
              </div>

              {/* Card de Aniversários do Mês - Total */}
              <div className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs md:text-sm text-secondary">Aniversários do Mês</p>
                        <p className="text-xl md:text-2xl font-bold text-yellow-600">
                          {birthdayStats.members}
                        </p>
                        {birthdayStats.preCadastros > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            +{birthdayStats.preCadastros} pré-cadastro{birthdayStats.preCadastros !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    <Cake className="w-5 h-5 md:w-6 md:h-6 text-yellow-600" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showMomentsListModal && (
        <Modal
          isOpen={showMomentsListModal}
          onClose={() => { setShowMomentsListModal(false); setMomentsListError(''); }}
          title="Lista de Momentos"
          size="lg"
          closeOnOverlayClick={false}
        >
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select
                value={momentsFilter}
                onChange={(e) => { setMomentsFilter(e.target.value); setMomentsPage(1); }}
                className="form-select"
                aria-label="Filtro de momento"
              >
                <option value="todos">Todos</option>
                <option value="manhã">Manhã</option>
                <option value="tarde">Tarde</option>
                <option value="noite">Noite</option>
              </select>
              <input
                type="text"
                className="form-input"
                placeholder="Filtrar por termo"
                value={momentsSearch}
                onChange={(e) => { setMomentsSearch(e.target.value); setMomentsPage(1); }}
                aria-label="Filtrar momentos"
              />
              <button className="btn btn-outline" onClick={loadMomentsList} aria-label="Atualizar lista">Atualizar</button>
            </div>
            {momentsListLoading ? (
              <div className="flex items-center justify-center py-6"><div className="loading" aria-hidden="true"></div><span className="ml-2 text-sm">Carregando...</span></div>
            ) : momentsListError ? (
              <p className="text-danger text-sm">{momentsListError}</p>
            ) : (
              <>
                <div className="space-y-3" role="list">
                  {momentsPaginated.map((item, i) => (
                    <div key={(item._id || item.createdAt || i) + '-' + i} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50" role="listitem">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{eventMomentLabel(item)}</h4>
                          <p className="text-sm text-gray-600">{formatBRDate(item.date || item.createdAt)}</p>
                          <p className="text-xs text-gray-500 mt-1">Presentes: {Number(item.presentTotal)||0} • Crianças: {Number(item.absentTotal)||0} • Visitantes: {Number(item.visitorsCount)||0}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleEditCulto(item)}
                            aria-label="Editar momento"
                          >
                            Editar
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteCulto(item)}
                            aria-label="Excluir momento"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {momentsFiltered.length > MOMENTS_LIMIT && (
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-secondary">
                      Mostrando {((momentsPage-1)*MOMENTS_LIMIT)+1} a {Math.min(momentsPage*MOMENTS_LIMIT, momentsFiltered.length)} de {momentsFiltered.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <button className="btn btn-sm btn-outline" onClick={()=>setMomentsPage(p => Math.max(1, p-1))} disabled={momentsPage===1}>Anterior</button>
                      <button className="btn btn-sm btn-outline" onClick={()=>setMomentsPage(p => (p*MOMENTS_LIMIT>=momentsFiltered.length? p : p+1))} disabled={momentsPage*MOMENTS_LIMIT>=momentsFiltered.length}>Próximo</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>
      )}

      {activeTab === 'statistics' && (
        <>
          {/* Estatísticas Detalhadas */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Membros por Perfil</h3>
                </div>
                <div className="card-body">
                  <div className="space-y-3">
                    {Object.entries(stats.byRole || {}).map(([role, count]) => {
                      const roleInfo = roles.find(r => r.value === role);
                      return (
                        <div key={role} className="flex items-center justify-between">
                          <span className="text-sm">{roleInfo?.label || role}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Card de Membros por Sexo */}
              {(() => {
                console.log('Checking byGender render condition:');
                console.log('stats?.byGender:', stats?.byGender);
                console.log('Object.keys(stats.byGender):', stats?.byGender ? Object.keys(stats.byGender) : 'undefined');
                return stats?.byGender && Object.keys(stats.byGender).length > 0;
              })() && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Membros por Sexo</h3>
                  </div>
                  <div className="card-body">
                    {(() => {
                      // Dados de teste para verificar se o Recharts funciona
                      const testGenderData = [
                        { name: 'Masculino', value: 45 },
                        { name: 'Feminino', value: 55 }
                      ];
                      
                      const genderData = [
                        { name: 'Masculino', value: stats.byGender.masculino || 0 },
                        { name: 'Feminino', value: stats.byGender.feminino || 0 }
                      ].filter(item => item.value > 0);
                      
                      console.log('Gender data for PieChart:', genderData);
                      console.log('Gender data length:', genderData.length);
                      console.log('Test gender data:', testGenderData);
                      
                      // Usar dados de teste se não houver dados reais
                      const dataToUse = genderData.length > 0 ? genderData : testGenderData;
                      
                      return (
                        <div>
          
                          <div 
                            className="h-48" 
                            style={{ 
                              width: '100%', 
                              height: '300px',
                              padding: '10px'
                            }}
                          >
            
                            <ResponsiveContainer width="100%" height={250}>
                              <PieChart width={400} height={250}>
                                <Pie
                                  data={dataToUse}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                  label={(entry) => `${entry.name}: ${entry.value}`}
                                >
                                  {dataToUse.map((entry, index) => (
                                    <Cell 
                                      key={`cell-${index}`} 
                                      fill={entry.name === 'Masculino' ? '#3b82f6' : '#ec4899'} 
                                    />
                                  ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Card de Membros por Faixa Etária */}
              {(() => {
                console.log('Checking byAgeGroup render condition:');
                console.log('stats:', stats);
                console.log('stats?.byAgeGroup:', stats?.byAgeGroup);
                console.log('Object.keys(stats.byAgeGroup):', stats?.byAgeGroup ? Object.keys(stats.byAgeGroup) : 'undefined');
                console.log('Object.keys(stats.byAgeGroup).length:', stats?.byAgeGroup ? Object.keys(stats.byAgeGroup).length : 0);
                return stats?.byAgeGroup && Object.keys(stats.byAgeGroup).length > 0;
              })() && (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Membros por Faixa Etária</h3>
                  </div>
                  <div className="card-body">
                    {(() => {
                      // Dados de teste para verificar se o Recharts funciona
                      const testData = [
                        { name: 'Teste A', value: 30 },
                        { name: 'Teste B', value: 20 },
                        { name: 'Teste C', value: 25 },
                        { name: 'Teste D', value: 25 }
                      ];
                      
                      const chartData = [
                        { name: '0-17 anos', value: stats.byAgeGroup['0-17'] || 0 },
                        { name: '18-29 anos', value: stats.byAgeGroup['18-29'] || 0 },
                        { name: '30-44 anos', value: stats.byAgeGroup['30-44'] || 0 },
                        { name: '45-59 anos', value: stats.byAgeGroup['45-59'] || 0 },
                        { name: '60+ anos', value: stats.byAgeGroup['60+'] || 0 }
                      ].filter(item => item.value > 0);
                      
                      console.log('Chart data for PieChart:', chartData);
                      console.log('Chart data length:', chartData.length);
                      console.log('Test data:', testData);
                      console.log('ResponsiveContainer rendering');
                      
                      // Usar dados de teste se não houver dados reais
                      const dataToUse = chartData.length > 0 ? chartData : testData;
                      
                      return (
                        <div>
          
                          <div 
                             className="h-48" 
                             style={{ 
                               width: '100%', 
                               height: '300px',
                               padding: '10px'
                             }}
                           >
             
                             <ResponsiveContainer width="100%" height={250}>
                               <PieChart width={400} height={250}>
                                 <Pie
                                   data={dataToUse}
                                   cx="50%"
                                   cy="50%"
                                   outerRadius={80}
                                   fill="#8884d8"
                                   dataKey="value"
                                   label={(entry) => `${entry.name}: ${entry.value}`}
                                 />
                                 <Tooltip />
                               </PieChart>
                             </ResponsiveContainer>
                           </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}


            </div>
          )}
        </>
      )}

      {activeTab === 'activities' && (
        <>
          {/* Momentos na Igreja */}
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between w-full">
                <h3 className="card-title">Momentos na Igreja</h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm btn-primary ${isOpeningCultoModal ? 'opacity-75 cursor-not-allowed' : ''}`}
                    aria-label="Adicionar momento de culto"
                    aria-busy={isOpeningCultoModal ? 'true' : 'false'}
                    onClick={async () => {
                      try {
                        setIsOpeningCultoModal(true);
                        setCultoOpenAnimate(true);
                        setShowMomentModal(false);
                        setShowCultoPreview(false);
                        setShowCultoDeleteModal(false);
                        setShowCultoModal(true);
                        setTimeout(() => {
                          setCultoOpenAnimate(false);
                          try { cultoDateRef.current && cultoDateRef.current.focus(); } catch {}
                        }, 0);
                      } catch (e) {
                        toast.error('Não foi possível abrir a modal de Culto');
                      } finally {
                        setIsOpeningCultoModal(false);
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Adicionar Momento</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="overflow-x-auto">
                <table className="w-full table-auto text-sm border rounded">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-center px-3 py-2">Data</th>
                      <th className="text-center px-3 py-2">Momento</th>
                      <th className="text-center px-3 py-2">Total de Adultos</th>
                      <th className="text-center px-3 py-2">Total de Crianças</th>
                      <th className="text-center px-3 py-2">Qtd. Visitantes</th>
                      <th className="text-center px-3 py-2">Total de Presentes</th>
                      <th className="text-center px-3 py-2">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cultosPaginated.map((c, idx) => {
                      const total = (Number(c.presentTotal)||0) + (Number(c.absentTotal)||0) + (Number(c.visitorsCount)||0);
                      return (
                        <tr key={(c._id || c.createdAt || idx) + '-' + idx} className={c.isNew ? 'bg-green-50' : ''}>
                          <td className="text-center px-3 py-2">{formatBRDate(c.date || c.createdAt)}</td>
                          <td className="text-center px-3 py-2">{eventMomentLabel(c)}</td>
                          <td className="text-center px-3 py-2">{Number(c.presentTotal)||0}</td>
                          <td className="text-center px-3 py-2">{Number(c.absentTotal)||0}</td>
                          <td className="text-center px-3 py-2">{Number(c.visitorsCount)||0}</td>
                          <td className="text-center px-3 py-2 font-medium">{total}</td>
                          <td className="text-center px-3 py-2">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                className="btn btn-sm btn-outline"
                                aria-label="Editar culto"
                                onClick={() => handleEditCulto(c)}
                              >
                                <Edit className="w-3 h-3" />
                                <span className="hidden sm:inline ml-1">Editar</span>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                aria-label="Excluir culto"
                                onClick={() => handleDeleteCulto(c)}
                              >
                                <Trash2 className="w-3 h-3" />
                                <span className="hidden sm:inline ml-1">Excluir</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {cultos.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-center text-secondary" colSpan={6}>Nenhum culto registrado</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {cultos.length > CULTOS_LIMIT && (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-secondary">
                    Mostrando {((cultosPage-1)*CULTOS_LIMIT)+1} a {Math.min(cultosPage*CULTOS_LIMIT, cultos.length)} de {cultos.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="btn btn-sm btn-outline" onClick={()=>setCultosPage(p => Math.max(1, p-1))} disabled={cultosPage===1}>Anterior</button>
                    <button className="btn btn-sm btn-outline" onClick={()=>setCultosPage(p => (p*CULTOS_LIMIT>=cultos.length? p : p+1))} disabled={cultosPage*CULTOS_LIMIT>=cultos.length}>Próximo</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'members' && (
        <>
          {/* Filtros */}
          <div className="card">
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    className="form-input"
                    value={filters.search}
                    onChange={(e) => handleFiltersChange({...filters, search: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <select
                    className="form-select"
                    value={filters.role}
                    onChange={(e) => handleFiltersChange({...filters, role: e.target.value})}
                  >
                    <option value="">Todos os perfis</option>
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <button
                    type="button"
                    className={`btn ${filters.birthdayMonthOnly ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => handleFiltersChange({...filters, birthdayMonthOnly: !filters.birthdayMonthOnly})}
                  >
                    <Cake className="w-4 h-4 mr-1" />
                    Aniversariantes do mês
                  </button>
                </div>

              </div>
            </div>
          </div>

          {/* Lista de Membros */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Membros</h2>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="loading"></div>
                  <span className="ml-2 text-sm">Carregando...</span>
                </div>
              ) : members.length > 0 ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="text-xs md:text-sm">Nome</th>
                        <th className="text-xs md:text-sm hidden sm:table-cell">Email</th>
                        <th className="text-xs md:text-sm hidden md:table-cell">Telefone</th>
                        <th className="text-xs md:text-sm">Perfil</th>

                        <th className="text-xs md:text-sm hidden lg:table-cell">Data de Nascimento</th>
                        {canEdit && <th className="text-xs md:text-sm">Ações</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member._id}>
                          <td className="text-xs md:text-sm">
                            <div className="font-medium flex items-center gap-2">
                              {/* Avatar */}
                              {member.avatar ? (
                                <img
                                  src={member.avatar}
                                  alt={member.name}
                                  style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid #e2e8f0' }}
                                />
                              ) : (
                                <div style={{
                                  width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                  background: getAvatarColor(member.name),
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '11px', fontWeight: '700', color: '#fff',
                                  border: '2px solid rgba(255,255,255,0.8)',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                                }}>
                                  {getInitials(member.name)}
                                </div>
                              )}
                              <div>
                                <div className="flex items-center gap-1">
                                  {(member.birthDate && birthMonthOf(member.birthDate) === currentMonthBR()) && (
                                    <Cake className="w-3 h-3 text-yellow-600" />
                                  )}
                                  {member.name}
                                </div>
                                <div className="sm:hidden text-xs text-secondary">{member.email}</div>
                                <div className="md:hidden lg:block text-xs text-secondary">
                                  {member.phone && <span>📞 {member.phone}</span>}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="hidden sm:table-cell text-xs md:text-sm">{member.email}</td>
                          <td className="hidden md:table-cell text-xs md:text-sm">{member.phone || '-'}</td>
                          <td>
                            <span className={`inline-block px-1 md:px-2 py-1 text-xs rounded ${
                              member.role === 'administracao' ? 'bg-error-color text-white' :
                              member.role === 'pastor' ? 'bg-warning-color text-white' :
                              member.role === 'secretario' || member.role === 'tesoureiro' ? 'bg-primary-color text-white' :
                              'bg-secondary-color text-white'
                            }`}>
                              <span className="sm:hidden">
                                {member.role === 'administracao' ? 'ADM' :
                                 member.role === 'pastor' ? 'PAS' :
                                 member.role === 'secretario' ? 'SEC' :
                                 member.role === 'tesoureiro' ? 'TES' : 'MEM'}
                              </span>
                              <span className="hidden sm:inline">
                                {roles.find(r => r.value === member.role)?.label || member.role}
                              </span>
                            </span>
                          </td>

                          <td className="hidden lg:table-cell text-xs md:text-sm">
                            {member.birthDate 
                              ? formatDateSafe(toInputDateValue(member.birthDate))
                              : '-'
                            }
                          </td>
                          {canEdit && (
                            <td>
                              <div className="flex gap-4">
                                <button
                                  onClick={() => handleView(member)}
                                  className="btn btn-sm btn-outline p-1 md:p-2"
                                >
                                  <FileText className="w-3 h-3 md:w-4 md:h-4 text-gray-600" />
                                  <span className="hidden sm:inline ml-1">Ver</span>
                                </button>
                                <button
                                  onClick={() => handleEdit(member)}
                                  className="btn btn-sm btn-outline p-1 md:p-2"
                                >
                                  <Edit className="w-3 h-3 md:w-4 md:h-4" />
                                  <span className="hidden sm:inline ml-1">Editar</span>
                                </button>
                                <button
                                  onClick={() => openPermissionsModal(member)}
                                  className={`btn btn-sm ${(
                                    member.permissions?.fichasEnabled ||
                                    member.permissions?.eventsManageEnabled
                                  ) ? 'btn-success' : 'btn-outline'} p-1 md:p-2`}
                                  aria-haspopup="dialog"
                                  aria-pressed={(
                                    member.permissions?.fichasEnabled ||
                                    member.permissions?.eventsManageEnabled
                                  ) ? 'true' : 'false'}
                                  aria-label="Abrir permissões do membro"
                                >
                                  <Settings className="w-3 h-3 md:w-4 md:h-4" />
                                  <span className="hidden sm:inline ml-1">Permissões</span>
                                </button>
                                {canEdit && (
                                  <button
                                    onClick={() => handleDelete(member)}
                                    className={`btn btn-sm ${(user?.role === 'administracao' || user?.role === 'secretario') ? 'btn-danger' : 'btn-outline'} p-1 md:p-2`}
                                    disabled={!(user?.role === 'administracao' || user?.role === 'secretario')}
                                    aria-disabled={(user?.role === 'administracao' || user?.role === 'secretario') ? 'false' : 'true'}
                                    title={(user?.role === 'administracao' || user?.role === 'secretario') ? undefined : 'Somente Administração e Secretaria'}
                                  >
                                    <Trash2 className="w-3 h-3 md:w-4 md:h-4" />
                                    <span className="hidden sm:inline ml-1">Excluir</span>
                                  </button>
                                )}
                                {canInvite && (
                                  member.emailVerified ? (
                                    <button type="button" className="btn btn-sm btn-success" disabled aria-label="Email verificado">
                                      <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleResendVerification(member._id)}
                                      className="btn btn-sm btn-primary"
                                      aria-label="Verificar email do membro"
                                    >
                                      📧 Verificar
                                    </button>
                                  )
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-secondary">Nenhum membro encontrado</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Controles de Paginação */}
          {!loading && members.length > 0 && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-secondary">
                    Mostrando {((pagination.currentPage - 1) * pagination.limit) + 1} a {Math.min(pagination.currentPage * pagination.limit, pagination.total)} de {pagination.total} membros
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="btn btn-sm btn-outline"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNumber;
                        if (pagination.totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                          pageNumber = pagination.totalPages - 4 + i;
                        } else {
                          pageNumber = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`btn btn-sm ${
                              pageNumber === pagination.currentPage
                                ? 'btn-primary'
                                : 'btn-outline'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="btn btn-sm btn-outline"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de Visualização de Membro */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewMember(null);
        }}
        title={viewMember ? `Dados de ${viewMember.name}` : 'Dados do Membro'}
        size="md"
        closeOnOverlayClick={true}
      >
        {viewMember && (
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-semibold">Nome:</span> {viewMember.name}
            </div>
            <div>
              <span className="font-semibold">Email:</span> {viewMember.email}
            </div>
            <div>
              <span className="font-semibold">Telefone:</span> {viewMember.phone || '-'}
            </div>
            <div>
              <span className="font-semibold">Nascimento:</span> {viewMember.birthDate ? formatDateSafe(toInputDateValue(viewMember.birthDate)) : '-'}
            </div>
            <div>
              <span className="font-semibold">Perfil:</span> {roles.find(r => r.value === viewMember.role)?.label || viewMember.role}
            </div>
            <div>
              <span className="font-semibold">Endereço:</span> {[viewMember.address?.street, viewMember.address?.city, viewMember.address?.state, viewMember.address?.zipCode].filter(Boolean).join(', ') || '-'}
            </div>
            <div>
              <span className="font-semibold">Aceitou Jesus:</span> {viewMember.acceptedJesus ? 'Sim' : 'Não'}
            </div>
            <div>
              <span className="font-semibold">Data da Conversão:</span> {viewMember.conversionDate ? formatDateSafe(toInputDateValue(viewMember.conversionDate)) : '-'}
            </div>
            <div>
              <span className="font-semibold">Batismo em Águas:</span> {viewMember.baptized ? 'Sim' : 'Não'}
            </div>
            <div>
              <span className="font-semibold">Data do Batismo:</span> {viewMember.baptismDate ? formatDateSafe(toInputDateValue(viewMember.baptismDate)) : '-'}
            </div>
            <div>
              <span className="font-semibold">Registro de Encontros:</span> {viewMember.encountersNotes || '-'}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Membro */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingMember(null);
          resetForm();
        }}
        title={editingMember ? 'Editar Membro' : 'Novo Membro'}
        size="lg"
        closeOnOverlayClick={false}
      >
        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'0' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

            {/* Seção: Dados Pessoais */}
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', paddingBottom:'8px', borderBottom:'1px solid #f1f5f9' }}>Dados Pessoais</div>

            <div>
              <label htmlFor="member-name" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Nome Completo *</label>
              <input id="member-name" type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              <div>
                <label htmlFor="member-marital-status" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Estado Civil</label>
                <select id="member-marital-status" value={formData.maritalStatus} onChange={(e) => setFormData({...formData, maritalStatus: e.target.value})}
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box', cursor:'pointer' }}>
                  <option value="">Selecione...</option>
                  <option value="solteiro">Solteiro(a)</option>
                  <option value="casado">Casado(a)</option>
                  <option value="divorciado">Divorciado(a)</option>
                  <option value="viuvo">Viúvo(a)</option>
                  <option value="uniao_estavel">União Estável</option>
                </select>
              </div>
              <div>
                <label htmlFor="member-gender" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Sexo</label>
                <select id="member-gender" value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box', cursor:'pointer' }}>
                  <option value="">Selecione...</option>
                  <option value="masculino">Masculino</option>
                  <option value="feminino">Feminino</option>
                </select>
              </div>
            </div>

            {/* Seção: Contato */}
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', paddingTop:'8px', paddingBottom:'8px', borderBottom:'1px solid #f1f5f9', marginTop:'4px' }}>Contato</div>

            <div>
              <label htmlFor="member-email" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Email *</label>
              <input id="member-email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              {editingMember && (
                <div style={{ marginTop:'6px' }}>
                  {editingMember.emailVerified ? (
                    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', fontSize:'12px', fontWeight:'600', color:'#059669', background:'#f0fdf4', padding:'3px 10px', borderRadius:'20px', border:'1px solid #86efac' }}>
                      <CheckCircle className="w-3 h-3" /> Verificado
                    </span>
                  ) : (
                    <button type="button" onClick={() => handleResendVerification(editingMember._id)} disabled={loading}
                      style={{ fontSize:'12px', fontWeight:'600', color:'#3b82f6', background:'#eff6ff', padding:'3px 10px', borderRadius:'20px', border:'1px solid #bfdbfe', cursor:'pointer' }}>
                      📧 Verificar email
                    </button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              <div>
                <label htmlFor="member-phone" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Telefone</label>
                <input id="member-phone" type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(00) 00000-0000"
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
              <div>
                <label htmlFor="member-birthdate" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data de Nascimento</label>
                <input id="member-birthdate" type="date" value={toInputDateValue(formData.birthDate)} onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
            </div>

            {/* Seção: Endereço */}
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', paddingTop:'8px', paddingBottom:'8px', borderBottom:'1px solid #f1f5f9', marginTop:'4px' }}>Endereço</div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <input type="text" placeholder="CEP" aria-label="CEP" value={formData.address.zipCode} onChange={handleCepChange}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', boxSizing:'border-box' }} />
              <input type="text" placeholder="Rua e número" aria-label="Rua e número" value={formData.address.street} onChange={(e) => setFormData({...formData, address:{...formData.address, street:e.target.value}})}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', boxSizing:'border-box' }} />
              <input type="text" placeholder="Cidade" aria-label="Cidade" value={formData.address.city} onChange={(e) => setFormData({...formData, address:{...formData.address, city:e.target.value}})}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', boxSizing:'border-box' }} />
              <input type="text" placeholder="Estado" aria-label="Estado" value={formData.address.state} onChange={(e) => setFormData({...formData, address:{...formData.address, state:e.target.value}})}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', boxSizing:'border-box' }} />
            </div>

            {/* Seção: Perfil */}
            <div style={{ fontSize:'11px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', paddingTop:'8px', paddingBottom:'8px', borderBottom:'1px solid #f1f5f9', marginTop:'4px' }}>Perfil de Acesso</div>

            <div>
              <label htmlFor="member-role" style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Perfil</label>
              <select id="member-role" value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})}
                disabled={user?.role !== 'administracao' && user?.role !== 'secretario'}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background: (user?.role !== 'administracao' && user?.role !== 'secretario') ? '#f1f5f9' : '#f8fafc', outline:'none', width:'100%', boxSizing:'border-box', cursor: (user?.role !== 'administracao' && user?.role !== 'secretario') ? 'not-allowed' : 'pointer' }}>
                {roles.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
            </div>

            {!editingMember && (
              <div style={{ background:'#f8fafc', borderRadius:'10px', padding:'12px 14px', border:'1px solid #e2e8f0', fontSize:'13px', color:'#64748b' }}>
                <strong style={{ color:'#475569' }}>Senha padrão:</strong> senha123 — o membro poderá alterar após o primeiro login.
              </div>
            )}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px', paddingTop:'16px', borderTop:'1px solid #f1f5f9' }}>
            <button type="button" onClick={() => { setShowModal(false); setEditingMember(null); resetForm(); }}
              style={{ padding:'10px 20px', borderRadius:'10px', border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontSize:'14px', fontWeight:'600', cursor:'pointer' }}>
              Cancelar
            </button>
            <button type="submit" style={{ padding:'10px 24px', borderRadius:'10px', border:'none', background:'#3b82f6', color:'#fff', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
              {editingMember ? 'Salvar alterações' : 'Criar Membro'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Relatórios */}
      <Modal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        title="Relatórios de Membros"
        size="lg"
        closeOnOverlayClick={false}
      >
        <div className="space-y-4">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-primary-color" />
                <div className="flex-1">
                  <h4 className="font-medium">Relatório Completo de Membros</h4>
                  <p className="text-sm text-secondary">Exportar todos os dados dos membros em formato CSV</p>
                </div>
                <button
                  onClick={generateReport}
                  className="btn btn-primary"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-success-color" />
                <div className="flex-1">
                  <h4 className="font-medium">Relatório de Estatísticas</h4>
                  <p className="text-sm text-secondary">Resumo estatístico dos membros por perfil</p>
                </div>
                <button
                  onClick={() => {
                    const statsData = [
                      ['Métrica', 'Valor'],
                      ['Total de Membros', stats?.total || 0],
                      ['Membros Recentes', stats?.recentMembers || 0],
      
                      ...Object.entries(stats?.byRole || {}).map(([role, count]) => [
                        `Membros - ${roles.find(r => r.value === role)?.label || role}`,
                        count
                      ])
                    ];
                    
                    const csv = statsData.map(row => row.join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `estatisticas-membros-${format(new Date(), 'yyyy-MM-dd')}.csv`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  }}
                  className="btn btn-success"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal de Cadastro Específico */}
      <Modal
        isOpen={showCadastroModal}
        onClose={() => {
          setShowCadastroModal(false);
          resetCadastroForm();
        }}
        title="Fichas"
        size="lg"
        closeOnOverlayClick={false}
      >
        <form onSubmit={handleCadastroSubmit}>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Nome Completo *</label>
              <input type="text" value={cadastroData.name} onChange={(e) => setCadastroData({...cadastroData, name: e.target.value})} required placeholder="Nome completo"
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
            </div>

            {/* Podemos mandar mensagem? */}
            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Podemos te mandar uma mensagem? *</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                {[{v:'sim',l:'Sim'},{v:'não',l:'Não'}].map(({v,l}) => (
                  <button key={v} type="button" onClick={() => setCadastroData({...cadastroData, canSendMessage: v})}
                    style={{ padding:'10px', borderRadius:'10px', border:`1.5px solid ${cadastroData.canSendMessage===v ? '#3b82f6' : '#e2e8f0'}`, background: cadastroData.canSendMessage===v ? '#eff6ff' : '#fff', color: cadastroData.canSendMessage===v ? '#1d4ed8' : '#94a3b8', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all 0.15s' }}>
                    {l}
                  </button>
                ))}
              </div>
              {cadastroData.canSendMessage === 'não' && (
                <div style={{ marginTop:'8px', background:'#f8fafc', borderRadius:'8px', padding:'10px 12px', fontSize:'13px', color:'#64748b', border:'1px solid #e2e8f0' }}>
                  Ok, não enviaremos mensagens. Se quiser, informe um telefone apenas para registro.
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>
                Telefone{cadastroData.canSendMessage === 'sim' ? ' *' : ''}
              </label>
              <input type="tel" value={cadastroData.phone} onChange={(e) => setCadastroData({...cadastroData, phone: e.target.value})} placeholder="(00) 00000-0000"
                required={cadastroData.canSendMessage === 'sim'}
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
            </div>

            {/* Tipo de Ficha */}
            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Tipo de Ficha</label>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                {[
                  {v:'visitante', l:'Visitando', color:'#3b82f6', bg:'#eff6ff'},
                  {v:'decisao', l:'Decisão', color:'#8b5cf6', bg:'#f5f3ff'},
                ].map(({v,l,color,bg}) => (
                  <button key={v} type="button"
                    onClick={() => setCadastroData({...cadastroData, type:v, visitDate: v==='visitante' ? getCurrentDate() : ''})}
                    style={{ padding:'12px', borderRadius:'10px', border:`1.5px solid ${cadastroData.type===v ? color : '#e2e8f0'}`, background: cadastroData.type===v ? bg : '#fff', color: cadastroData.type===v ? color : '#94a3b8', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all 0.15s' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Data da visita */}
            {cadastroData.type === 'visitante' && (
              <div>
                <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data da Visita</label>
                <input type="date" value={toInputDateValue(cadastroData.visitDate)} onChange={(e) => setCadastroData({...cadastroData, visitDate: e.target.value})} required
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
            )}

            {/* Campos de Decisão */}
            {cadastroData.type === 'decisao' && (
              <>
                <div>
                  <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Está se reconciliando?</label>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                    {[{v:'sim',l:'Sim'},{v:'não',l:'Não'}].map(({v,l}) => (
                      <button key={v} type="button"
                        onClick={() => setCadastroData({...cadastroData, isReconciling:v, reconcilingDate: v==='sim' ? getCurrentDate() : ''})}
                        style={{ padding:'10px', borderRadius:'10px', border:`1.5px solid ${cadastroData.isReconciling===v ? '#f59e0b' : '#e2e8f0'}`, background: cadastroData.isReconciling===v ? '#fffbeb' : '#fff', color: cadastroData.isReconciling===v ? '#d97706' : '#94a3b8', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all 0.15s' }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {cadastroData.isReconciling === 'sim' && (
                  <div>
                    <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data de Reconciliação</label>
                    <input type="date" value={toInputDateValue(cadastroData.reconcilingDate)} onChange={(e) => setCadastroData({...cadastroData, reconcilingDate: e.target.value})}
                      style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
                  </div>
                )}

                {cadastroData.isReconciling === 'não' && (
                  <div>
                    <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Está aceitando a Jesus?</label>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                      {[{v:'sim',l:'Sim'},{v:'não',l:'Não'}].map(({v,l}) => (
                        <button key={v} type="button"
                          onClick={() => setCadastroData({...cadastroData, acceptedJesus:v, acceptedJesusDate: v==='sim' ? getCurrentDate() : ''})}
                          style={{ padding:'10px', borderRadius:'10px', border:`1.5px solid ${cadastroData.acceptedJesus===v ? '#10b981' : '#e2e8f0'}`, background: cadastroData.acceptedJesus===v ? '#f0fdf4' : '#fff', color: cadastroData.acceptedJesus===v ? '#059669' : '#94a3b8', fontSize:'14px', fontWeight:'700', cursor:'pointer', transition:'all 0.15s' }}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {cadastroData.isReconciling === 'não' && cadastroData.acceptedJesus === 'sim' && (
                  <div>
                    <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data de Aceitação</label>
                    <input type="date" value={toInputDateValue(cadastroData.acceptedJesusDate)} onChange={(e) => setCadastroData({...cadastroData, acceptedJesusDate: e.target.value})}
                      style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px', paddingTop:'16px', borderTop:'1px solid #f1f5f9' }}>
            <button type="button" onClick={() => { setShowCadastroModal(false); resetCadastroForm(); }}
              style={{ padding:'10px 20px', borderRadius:'10px', border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontSize:'14px', fontWeight:'600', cursor:'pointer' }}>
              Cancelar
            </button>
            <button type="submit" style={{ padding:'10px 24px', borderRadius:'10px', border:'none', background:'#3b82f6', color:'#fff', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
              Salvar Ficha
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setMemberToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir ${memberToDelete?.name}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
         confirmVariant="danger"
       />

      {/* Modal de Edição de Visitante */}
      <Modal
        isOpen={showEditVisitanteModal}
        onClose={() => {
          setShowEditVisitanteModal(false);
          setEditingVisitante(null);
        }}
        title="Editar Visitante"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          saveEditVisitante();
        }}>
          <div className="form-group">
            <label className="form-label">Nome Completo</label>
            <input
              type="text"
              className="form-input"
              value={cadastroData.name}
              onChange={(e) => setCadastroData({...cadastroData, name: e.target.value})}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Telefone{cadastroData.canSendMessage === 'sim' ? ' *' : ''}</label>
            <input
              type="tel"
              className="form-input"
              value={cadastroData.phone}
              onChange={(e) => setCadastroData({...cadastroData, phone: e.target.value})}
              required={cadastroData.canSendMessage === 'sim'}
            />
          </div>

          {cadastroData.canSendMessage === 'não' && (
            <div className="p-3 bg-background-color rounded-md mt-2 text-sm text-secondary">
              Ok, não enviaremos mensagens. Se quiser, informe um telefone apenas para registro.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Pode enviar mensagem?</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="canSendMessage"
                  value="sim"
                  checked={cadastroData.canSendMessage === 'sim'}
                  onChange={(e) => setCadastroData({...cadastroData, canSendMessage: e.target.value})}
                  className="mr-2"
                />
                Sim
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="canSendMessage"
                  value="não"
                  checked={cadastroData.canSendMessage === 'não'}
                  onChange={(e) => setCadastroData({...cadastroData, canSendMessage: e.target.value})}
                  className="mr-2"
                />
                Não
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Data da Visita</label>
            <input
              type="date"
              className="form-input"
              value={toInputDateValue(cadastroData.visitDate)}
              onChange={(e) => setCadastroData({...cadastroData, visitDate: e.target.value})}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-color">
            <button
              type="button"
              onClick={() => {
                setShowEditVisitanteModal(false);
                setEditingVisitante(null);
              }}
              className="btn btn-outline flex-1 sm:flex-none"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary flex-1 sm:flex-none"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Edição de Decisão */}
      <Modal
        isOpen={showEditDecisaoModal}
        onClose={() => {
          setShowEditDecisaoModal(false);
          setEditingDecisao(null);
        }}
        title="Editar Decisão"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          saveEditDecisao();
        }}>
          <div className="form-group">
            <label className="form-label">Nome Completo</label>
            <input
              type="text"
              className="form-input"
              value={cadastroData.name}
              onChange={(e) => setCadastroData({...cadastroData, name: e.target.value})}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Telefone{cadastroData.canSendMessage === 'sim' ? ' *' : ''}</label>
            <input
              type="tel"
              className="form-input"
              value={cadastroData.phone}
              onChange={(e) => setCadastroData({...cadastroData, phone: e.target.value})}
              required={cadastroData.canSendMessage === 'sim'}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Pode enviar mensagem?</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="canSendMessage"
                  value="sim"
                  checked={cadastroData.canSendMessage === 'sim'}
                  onChange={(e) => setCadastroData({...cadastroData, canSendMessage: e.target.value})}
                  className="mr-2"
                />
                Sim
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="canSendMessage"
                  value="não"
                  checked={cadastroData.canSendMessage === 'não'}
                  onChange={(e) => setCadastroData({...cadastroData, canSendMessage: e.target.value})}
                  className="mr-2"
                />
                Não
              </label>
            </div>
          </div>

          {/* Data da Visita removida do modal de edição de decisão */}

          <div className="form-group">
            <label className="form-label">Está se reconciliando?</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isReconciling"
                  value="sim"
                  checked={cadastroData.isReconciling === 'sim'}
                  onChange={(e) => setCadastroData({...cadastroData, isReconciling: e.target.value})}
                  className="mr-2"
                />
                Sim
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="isReconciling"
                  value="não"
                  checked={cadastroData.isReconciling === 'não'}
                  onChange={(e) => setCadastroData({...cadastroData, isReconciling: e.target.value})}
                  className="mr-2"
                />
                Não
              </label>
            </div>
          </div>

          {cadastroData.isReconciling === 'sim' && (
            <div className="form-group">
              <label className="form-label">Data da Reconciliação</label>
              <input
                type="date"
                className="form-input"
                value={toInputDateValue(cadastroData.reconcilingDate)}
                onChange={(e) => setCadastroData({...cadastroData, reconcilingDate: e.target.value})}
              />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Aceitou Jesus?</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="acceptedJesus"
                  value="sim"
                  checked={cadastroData.acceptedJesus === 'sim'}
                  onChange={(e) => setCadastroData({...cadastroData, acceptedJesus: e.target.value})}
                  className="mr-2"
                />
                Sim
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="acceptedJesus"
                  value="não"
                  checked={cadastroData.acceptedJesus === 'não'}
                  onChange={(e) => setCadastroData({...cadastroData, acceptedJesus: e.target.value})}
                  className="mr-2"
                />
                Não
              </label>
            </div>
          </div>

          {cadastroData.acceptedJesus === 'sim' && (
            <div className="form-group">
              <label className="form-label">Data da Aceitação</label>
              <input
                type="date"
                className="form-input"
                value={toInputDateValue(cadastroData.acceptedJesusDate)}
                onChange={(e) => setCadastroData({...cadastroData, acceptedJesusDate: e.target.value})}
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-border-color">
            <button
              type="button"
              onClick={() => {
                setShowEditDecisaoModal(false);
                setEditingDecisao(null);
              }}
              className="btn btn-outline flex-1 sm:flex-none"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary flex-1 sm:flex-none"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmação de Exclusão de Visitante */}
      <ConfirmModal
        isOpen={showDeleteVisitanteModal}
        onClose={() => {
          setShowDeleteVisitanteModal(false);
          setVisitanteToDelete(null);
        }}
        onConfirm={confirmDeleteVisitante}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o visitante ${visitanteToDelete?.name}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmVariant="danger"
      />

      {/* Modal de Confirmação de Exclusão de Decisão */}
      <ConfirmModal
        isOpen={showDeleteDecisaoModal}
        onClose={() => {
          setShowDeleteDecisaoModal(false);
          setDecisaoToDelete(null);
        }}
        onConfirm={confirmDeleteDecisao}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a decisão de ${decisaoToDelete?.name}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmVariant="danger"
      />

      {/* Modal de Edição de Pré-cadastro */}
      <Modal
        isOpen={showEditPreCadastroModal}
        onClose={() => {
          setShowEditPreCadastroModal(false);
          setEditingPreCadastro(null);
          setPreCadastroData({ name: '', phone: '', birthDate: '' });
        }}
        title="Editar Pré-cadastro"
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); saveEditPreCadastro(); }} className="space-y-4">
          {/* Nome */}
          <div className="form-group">
            <label className="form-label">Nome Completo *</label>
            <input
              type="text"
              className="form-input"
              value={preCadastroData.name}
              onChange={(e) => setPreCadastroData({...preCadastroData, name: e.target.value})}
              required
              placeholder="Digite o nome completo"
            />
          </div>

          {/* Telefone */}
          <div className="form-group">
            <label className="form-label">Telefone *</label>
            <input
              type="tel"
              className="form-input"
              value={preCadastroData.phone}
              onChange={(e) => setPreCadastroData({...preCadastroData, phone: e.target.value})}
              required
              placeholder="(00) 00000-0000"
            />
          </div>

          {/* Data de Nascimento */}
          <div className="form-group">
            <label className="form-label">Data de Nascimento *</label>
            <input
              type="date"
              className="form-input"
              value={toInputDateValue(preCadastroData.birthDate)}
              onChange={(e) => setPreCadastroData({...preCadastroData, birthDate: e.target.value})}
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-color">
            <button
              type="button"
              onClick={() => {
                setShowEditPreCadastroModal(false);
                setEditingPreCadastro(null);
                setPreCadastroData({ name: '', phone: '', birthDate: '' });
              }}
              className="btn btn-outline flex-1 sm:flex-none"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn-primary flex-1 sm:flex-none"
            >
              Salvar Alterações
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmação de Exclusão de Pré-cadastro */}
      <ConfirmModal
        isOpen={showDeletePreCadastroModal}
        onClose={() => {
          setShowDeletePreCadastroModal(false);
          setPreCadastroToDelete(null);
        }}
        onConfirm={confirmDeletePreCadastro}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o pré-cadastro de ${preCadastroToDelete?.name}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmVariant="danger"
      />

      {/* Modal de Pré-cadastro */}
      <Modal
        isOpen={showPreCadastroModal}
        onClose={() => {
          setShowPreCadastroModal(false);
          setPreCadastroData({ name: '', phone: '', birthDate: '' });
        }}
        title="Pré-cadastro"
        size="md"
      >
        <form onSubmit={handlePreCadastroSubmit}>
          <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
            <div>
              <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Nome Completo *</label>
              <input type="text" value={preCadastroData.name} onChange={(e) => setPreCadastroData({...preCadastroData, name: e.target.value})} required placeholder="Nome completo"
                style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              <div>
                <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Telefone *</label>
                <input type="tel" value={preCadastroData.phone} onChange={(e) => setPreCadastroData({...preCadastroData, phone: e.target.value})} required placeholder="(00) 00000-0000"
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:'12px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px' }}>Data de Nascimento *</label>
                <input type="date" value={toInputDateValue(preCadastroData.birthDate)} onChange={(e) => setPreCadastroData({...preCadastroData, birthDate: e.target.value})} required
                  style={{ padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #e2e8f0', fontSize:'14px', color:'#1e293b', background:'#f8fafc', outline:'none', width:'100%', boxSizing:'border-box' }} />
              </div>
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', marginTop:'24px', paddingTop:'16px', borderTop:'1px solid #f1f5f9' }}>
            <button type="button" onClick={() => { setShowPreCadastroModal(false); setPreCadastroData({name:'',phone:'',birthDate:''}); }}
              style={{ padding:'10px 20px', borderRadius:'10px', border:'1.5px solid #e2e8f0', background:'#fff', color:'#64748b', fontSize:'14px', fontWeight:'600', cursor:'pointer' }}>
              Cancelar
            </button>
            <button type="submit" style={{ padding:'10px 24px', borderRadius:'10px', border:'none', background:'#3b82f6', color:'#fff', fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
              Salvar Pré-cadastro
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de Permissões de Membro */}
      <Modal
        isOpen={showPermissionsModal}
        onClose={() => { setShowPermissionsModal(false); setPermissionsTarget(null); }}
        title={permissionsTarget ? `Permissões de ${permissionsTarget.name}` : 'Permissões'}
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); savePermissions(); }} className="space-y-4" aria-live="polite">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <label className="form-label">Fichas</label>
                  <p className="text-xs text-secondary">Permite acesso à função Fichas no Home do membro</p>
                </div>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={permissionsForm.fichasEnabled ? 'true' : 'false'}
                  onChange={(e) => setPermissionsForm({ ...permissionsForm, fichasEnabled: e.target.checked })}
                  checked={permissionsForm.fichasEnabled}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <label className="form-label">Gerenciar Eventos</label>
                  <p className="text-xs text-secondary">Permite criar/editar/excluir eventos no Home</p>
                </div>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={permissionsForm.eventsManageEnabled ? 'true' : 'false'}
                  onChange={(e) => setPermissionsForm({ ...permissionsForm, eventsManageEnabled: e.target.checked })}
                  checked={permissionsForm.eventsManageEnabled}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <label className="form-label">Momentos</label>
                  <p className="text-xs text-secondary">Permite acesso aos Momentos (Cultos) no Home</p>
                </div>
                <input
                  type="checkbox"
                  role="switch"
                  aria-checked={permissionsForm.momentsEnabled ? 'true' : 'false'}
                  onChange={(e) => setPermissionsForm({ ...permissionsForm, momentsEnabled: e.target.checked })}
                  checked={permissionsForm.momentsEnabled}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-color">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => { setShowPermissionsModal(false); setPermissionsTarget(null); }}
              disabled={permissionsLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              aria-busy={permissionsLoading ? 'true' : 'false'}
              disabled={permissionsLoading}
            >
              {permissionsLoading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>

    </div>
  );
};

export default Members;
